/* ============================================
   SMGLR Theme - JavaScript
   ============================================ */

(function() {
  'use strict';

  /* === Smooth Scroll: Lenis + GSAP ScrollTrigger === */
  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var lenis = null;

  if (typeof Lenis !== 'undefined' && !prefersReducedMotion) {
    try {
      lenis = new Lenis({
        duration: 1.15,
        easing: function(t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
        smoothWheel: true,
        smoothTouch: false,
        touchMultiplier: 2,
        wheelMultiplier: 1
      });
      function rafLoop(time) { lenis.raf(time); requestAnimationFrame(rafLoop); }
      requestAnimationFrame(rafLoop);

      if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
        gsap.registerPlugin(ScrollTrigger);
        if (typeof SplitText !== 'undefined') {
          gsap.registerPlugin(SplitText);
        }
        lenis.on('scroll', ScrollTrigger.update);
        gsap.ticker.add(function(time) { lenis.raf(time * 1000); });
        gsap.ticker.lagSmoothing(0);

        // Recalculate trigger positions once images / fonts have loaded.
        // Without this, triggers register against a shorter "loading" page
        // and below-the-fold animations fire immediately on page load.
        window.addEventListener('load', function() {
          ScrollTrigger.refresh();
        });
        // Extra refresh after layout settles (lazy-loaded images, fonts, etc.)
        setTimeout(function() { if (window.ScrollTrigger) ScrollTrigger.refresh(); }, 600);
        setTimeout(function() { if (window.ScrollTrigger) ScrollTrigger.refresh(); }, 2000);
      }

      window.lenis = lenis;
    } catch (e) {
      console.warn('[Lenis] init failed, falling back to native scroll:', e);
      lenis = null;
    }
  }

  window.smoothScrollTo = function(target, opts) {
    opts = opts || {};
    if (lenis) {
      lenis.scrollTo(target, { duration: opts.duration || 1.2, offset: opts.offset || 0 });
    } else {
      var el = typeof target === 'string' ? document.querySelector(target) : target;
      if (typeof target === 'number') {
        window.scrollTo({ top: target, behavior: 'smooth' });
      } else if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  /* === Scroll Reveal Animations (data-animate) === */
  function initScrollReveals() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined' || prefersReducedMotion) {
      document.querySelectorAll('[data-animate]').forEach(function(el) {
        el.style.opacity = '1';
        el.style.transform = 'none';
      });
      return;
    }
    // Parent stagger pattern: a wrapper with [data-animate-stagger] animates
    // its direct children in a single coordinated cascade. Works perfectly for
    // grids of cards (testimonials, blog posts, products) where individual
    // triggers would all fire at once.
    document.querySelectorAll('[data-animate-stagger]:not([data-animate-init])').forEach(function(parent) {
      parent.setAttribute('data-animate-init', '');
      var children = parent.children;
      if (!children.length) return;
      var stagger = parseFloat(parent.getAttribute('data-animate-stagger')) || 0.12;
      gsap.fromTo(children,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 0.9,
          ease: 'power3.out',
          stagger: stagger,
          scrollTrigger: { trigger: parent, start: 'top 80%', toggleActions: 'play none none none' },
          onComplete: function() {
            parent.setAttribute('data-animate-done', '');
            gsap.set(children, { clearProps: 'transform,willChange' });   // see note below
          }
        }
      );
      // Mark each child so the per-element loop below skips them.
      Array.prototype.forEach.call(children, function(c) { c.setAttribute('data-animate-init', ''); });
    });

    document.querySelectorAll('[data-animate]:not([data-animate-init])').forEach(function(el) {
      el.setAttribute('data-animate-init', '');
      var variant = el.getAttribute('data-animate') || 'up';
      var delay = parseFloat(el.getAttribute('data-animate-delay') || '0');
      var from = { opacity: 0 };
      var to = { opacity: 1, x: 0, y: 0, scale: 1 };
      if (variant === 'up' || variant === '') from.y = 50;
      if (variant === 'scale') from.scale = 0.92;
      if (variant === 'left') from.x = -60;
      if (variant === 'right') from.x = 60;

      gsap.fromTo(el, from, Object.assign({}, to, {
        duration: 1,
        ease: 'power3.out',
        delay: delay,
        scrollTrigger: { trigger: el, start: 'top 80%', toggleActions: 'play none none none' },
        onComplete: function() {
          el.setAttribute('data-animate-done', '');
          /* Drop the leftover transform. GSAP finishes on translate3d(0,0,0) scale(1) and
             leaves it applied, which keeps the element on its own composited layer for the
             rest of the session. Whatever was rasterised into that layer stays at the
             layer's raster scale, so SVG artwork and outlined text read soft — the PDP tab
             block carries data-animate, so this softened every Bean Guide slide. Clearing
             the transform returns it to normal painting; opacity stays inline at 1, so the
             element remains visible. */
          gsap.set(el, { clearProps: 'transform,willChange' });
        }
      }));
    });
  }
  // Defer scroll reveal init until window.load so trigger positions are
  // calculated against the final laid-out page (after images / fonts load).
  if (document.readyState === 'complete') {
    initScrollReveals();
  } else {
    window.addEventListener('load', initScrollReveals);
  }
  // Also re-scan after async sections inject content (recommendations, etc.)
  setTimeout(initScrollReveals, 2000);
  window.initScrollReveals = initScrollReveals;

  // Fallback: if GSAP never loaded or failed, show all hidden elements after 4s
  setTimeout(function() {
    if (typeof gsap === 'undefined') {
      document.documentElement.classList.add('no-gsap');
    }
  }, 4000);

  /* === Advanced Scroll Animations: parallax, zoom, word-split, counters === */
  function gsapReady() {
    return typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined' && !prefersReducedMotion;
  }

  // Parallax: any element with [data-parallax] (or [data-parallax="0.4"]) translates
  // its yPercent based on scroll position. Negative speed = moves up faster than scroll.
  function initParallax() {
    if (!gsapReady()) return;
    document.querySelectorAll('[data-parallax]:not([data-parallax-init])').forEach(function(el) {
      el.setAttribute('data-parallax-init', '');
      var speed = parseFloat(el.getAttribute('data-parallax')) || 0.3;
      gsap.to(el, {
        yPercent: -speed * 100,
        ease: 'none',
        scrollTrigger: {
          trigger: el.closest('section') || el,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true
        }
      });
    });
  }

  // Zoom-on-scroll: any element with [data-zoom-scroll] scales as you scroll past it.
  function initZoomScroll() {
    if (!gsapReady()) return;
    document.querySelectorAll('[data-zoom-scroll]:not([data-zoom-init])').forEach(function(el) {
      el.setAttribute('data-zoom-init', '');
      var endScale = parseFloat(el.getAttribute('data-zoom-scroll')) || 1.15;
      gsap.fromTo(el,
        { scale: 1 },
        {
          scale: endScale,
          ease: 'none',
          scrollTrigger: {
            trigger: el.closest('section') || el,
            start: 'top top',
            end: 'bottom top',
            scrub: true
          }
        }
      );
    });
  }

  // Word splitting + scrub fade. Splits only direct text nodes (preserves child
  // elements like inline images). Each word fades from 0.2 -> 1 opacity tied
  // to scroll position. Apply via [data-split-words] on the wrapper element.
  function splitNodeIntoWords(el) {
    var children = Array.prototype.slice.call(el.childNodes);
    children.forEach(function(node) {
      if (node.nodeType === Node.TEXT_NODE) {
        var text = node.textContent;
        if (!text.trim()) return;
        var frag = document.createDocumentFragment();
        var parts = text.split(/(\s+)/);
        parts.forEach(function(part) {
          if (/\s+/.test(part)) {
            frag.appendChild(document.createTextNode(part));
          } else if (part.length) {
            var span = document.createElement('span');
            span.className = 'split-word';
            span.textContent = part;
            frag.appendChild(span);
          }
        });
        node.parentNode.replaceChild(frag, node);
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        if (node.tagName === 'BR') return;
        // Treat media as their own "word" so they fade with the text.
        if (node.tagName === 'IMG' || node.tagName === 'A' || node.tagName === 'SVG' || node.tagName === 'VIDEO') {
          node.classList.add('split-word');
          return;
        }
        splitNodeIntoWords(node);
      }
    });
  }
  // Use GSAP SplitText if available; fall back to the custom splitter otherwise.
  // Stores the SplitText instance on the element so the brand-story pin function
  // can find the resulting word/char elements.
  function performSplit(el, mode) {
    if (typeof SplitText !== 'undefined') {
      try {
        var split = new SplitText(el, {
          type: mode === 'chars' ? 'words,chars' : 'words',
          wordsClass: 'split-word',
          charsClass: 'split-char',
          // Keep inline images, links, and SVGs intact — SplitText preserves them
          // automatically since it only operates on text nodes.
          smartWrap: true
        });
        el._splitInstance = split;
        // Tag any inline media so they participate in the fade.
        el.querySelectorAll('img, a, svg, video').forEach(function(node) {
          node.classList.add(mode === 'chars' ? 'split-char' : 'split-word');
        });
        return split;
      } catch (e) {
        console.warn('[SplitText] failed, using custom splitter:', e);
      }
    }
    // Fallback: custom splitter (word-only, image-aware).
    splitNodeIntoWords(el);
    return null;
  }

  function initSplitWords() {
    if (!gsapReady()) return;
    document.querySelectorAll('[data-split-words]:not([data-split-init])').forEach(function(el) {
      el.setAttribute('data-split-init', '');
      performSplit(el, 'words');
      var targets = el.querySelectorAll('.split-word');
      if (!targets.length) return;
      gsap.fromTo(targets,
        { opacity: 0.18 },
        {
          opacity: 1,
          stagger: 0.05,
          ease: 'none',
          scrollTrigger: {
            trigger: el,
            start: 'top 80%',
            end: 'bottom 50%',
            scrub: 0.6
          }
        }
      );
    });
  }

  // Character-by-character cascade for [data-split-chars] elements.
  // Triggers once on viewport entry rather than scrub — better for headlines.
  function initSplitChars() {
    if (!gsapReady() || typeof SplitText === 'undefined') return;
    document.querySelectorAll('[data-split-chars]:not([data-split-chars-init])').forEach(function(el) {
      el.setAttribute('data-split-chars-init', '');
      performSplit(el, 'chars');
      var chars = el.querySelectorAll('.split-char');
      if (!chars.length) return;
      gsap.fromTo(chars,
        { opacity: 0, y: 20, rotateX: -40 },
        {
          opacity: 1,
          y: 0,
          rotateX: 0,
          duration: 0.6,
          stagger: 0.025,
          ease: 'power3.out',
          scrollTrigger: { trigger: el, start: 'top 80%', toggleActions: 'play none none none' }
        }
      );
    });
  }

  // Counter tickers: <span data-counter="2890">2890</span> animates 0 -> 2890
  // when it enters the viewport.
  function initCounters() {
    if (!gsapReady()) {
      // Fallback: leave the static text in place.
      return;
    }
    document.querySelectorAll('[data-counter]:not([data-counter-init])').forEach(function(el) {
      el.setAttribute('data-counter-init', '');
      var target = parseFloat(el.getAttribute('data-counter')) || parseFloat(el.textContent.replace(/[^\d.]/g, '')) || 0;
      var decimals = (el.getAttribute('data-counter-decimals') | 0);
      var prefix = el.getAttribute('data-counter-prefix') || '';
      var suffix = el.getAttribute('data-counter-suffix') || '';
      var duration = parseFloat(el.getAttribute('data-counter-duration')) || 1.6;
      var obj = { val: 0 };
      el.textContent = prefix + (0).toFixed(decimals) + suffix;
      gsap.to(obj, {
        val: target,
        duration: duration,
        ease: 'power2.out',
        scrollTrigger: { trigger: el, start: 'top 90%', toggleActions: 'play none none none' },
        onUpdate: function() {
          var v = obj.val;
          var formatted = decimals > 0 ? v.toFixed(decimals) : Math.floor(v).toLocaleString('en-IN');
          el.textContent = prefix + formatted + suffix;
        }
      });
    });
  }

  // Brand story: the paragraph reveals word-by-word ONCE, automatically, when the section scrolls
  // into view — no pin/anchor and no scroll-scrub. It plays through on a timer (a staggered "wave"
  // of words lighting up) and stays revealed; it never reverses or replays on scroll-up. (Reduced
  // motion / no-GSAP never reach here — gsapReady() is false — so the CSS fallbacks show the text.)
  function initBrandStory() {
    if (!gsapReady()) return;
    var section = document.querySelector('.about-section');
    if (!section || section.hasAttribute('data-story-init')) return;
    section.setAttribute('data-story-init', '');

    var textBlock = section.querySelector('[data-split-words]');
    if (!textBlock) return;

    // Split into words. initSplitWords (earlier in the pipeline) also attaches a plain scroll-scrub
    // to this element — drop it and its tween so it doesn't fight our own play-once reveal below.
    initSplitWords();
    if (window.ScrollTrigger) {
      ScrollTrigger.getAll().forEach(function (st) { if (st.trigger === textBlock) st.kill(); });
    }
    var words = textBlock.querySelectorAll('.split-word');
    gsap.killTweensOf(words);
    textBlock.removeAttribute('data-story-pending');

    if (!words.length) { gsap.set(textBlock, { opacity: 1 }); return; }

    // Dim to start; then reveal word-by-word automatically once the section enters view. The
    // time-based stagger gives the progressive "wave"; it plays ONCE and never reverses.
    gsap.set(words, { opacity: 0.14 });

    var ctaWrap = section.querySelector('.about-section__cta-wrap');

    gsap.to(words, {
      opacity: 1,
      ease: 'power1.out',
      duration: 0.5,
      stagger: 0.05,
      scrollTrigger: {
        trigger: section,
        start: 'top 75%',   // begin as the section scrolls into view
        once: true          // play through once; no reverse, no replay
      },
      onComplete: function () {
        // Fade in the CTA button once the last word has revealed
        if (ctaWrap) {
          gsap.to(ctaWrap, { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' });
        }
      }
    });
  }

  // Character burn: dissolves a text element like burning paper, with real combustion behaviour.
  // TWO layers, both absolutely positioned over the element:
  //   1) a 2D canvas for the PAPER (source-over): text chars black at the advancing front then burns
  //      to tattered holes; ash flakes fall, smoke + embers rise (buoyancy / gravity / curl turbulence).
  //   2) a WebGL canvas for the FIRE (mix-blend-mode:screen -> real additive glow over the page): a
  //      fragment shader gathers the burning fuel below each pixel and lets it rise as continuous,
  //      turbulent flame through a black-body (orange->yellow->white) palette. If WebGL is unavailable
  //      it falls back to 2D flame tongues on the paper canvas.
  // Ignition is SEEDED at the bottom-centre (from the seal below) and propagates UP fast / sideways
  // slower with fractal-noise fingers, so the paragraph catches from the middle-bottom and spreads to
  // the top and out to the edges. Calls onDone() once ~82% has burned away so the story reveals
  // without overlap. Falls back to hiding the element on reduced-motion or any error.
  function burnText(el, onDone) {
    var revealed = false;
    function reveal() { if (!revealed) { revealed = true; if (onDone) onDone(); } }
    if (!el) { reveal(); return; }
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { try { el.style.display = 'none'; } catch (e) {} reveal(); return; }
    try {
      var rect = el.getBoundingClientRect();
      if (rect.width < 2 || rect.height < 2) { el.style.display = 'none'; reveal(); return; }
      var cs = getComputedStyle(el);
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var small = Math.min(window.innerWidth, window.innerHeight) < 760;
      var text = (el.textContent || '').replace(/\s+/g, ' ').trim();
      if (cs.textTransform === 'uppercase') text = text.toUpperCase();
      var padTop = Math.round(rect.height * 1.7), padBottom = Math.round(rect.height * 1.5), padX = Math.round(rect.height * 0.85);
      var cw = Math.ceil(rect.width) + padX * 2, ch = Math.ceil(rect.height) + padTop + padBottom;
      var left = rect.left + window.pageXOffset - padX, top = rect.top + window.pageYOffset - padTop;
      var bw = Math.round(cw * dpr), bh = Math.round(ch * dpr);
      function mkCanvas(z, blend) {
        var c = document.createElement('canvas'); c.width = bw; c.height = bh; c.setAttribute('aria-hidden', 'true');
        c.style.cssText = 'position:absolute;left:' + left + 'px;top:' + top + 'px;width:' + cw + 'px;height:' + ch + 'px;pointer-events:none;z-index:' + z + (blend ? ';mix-blend-mode:' + blend : '');
        document.body.appendChild(c); return c;
      }

      // ---- render the paper text once (also the fuel/alpha source for both layers) ----
      var textCv = document.createElement('canvas'); textCv.width = bw; textCv.height = bh;
      var tg = textCv.getContext('2d'); tg.scale(dpr, dpr);
      var fontStr = cs.fontStyle + ' ' + cs.fontWeight + ' ' + cs.fontSize + ' ' + cs.fontFamily;
      tg.font = fontStr; tg.textAlign = 'center'; tg.textBaseline = 'middle';
      if ('letterSpacing' in tg) { try { tg.letterSpacing = cs.letterSpacing; } catch (e) {} }
      var availW = Math.max(10, rect.width - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0));
      var words = text.split(' '), lines = [], acc = '';
      for (var wi = 0; wi < words.length; wi++) { var tl = acc ? acc + ' ' + words[wi] : words[wi]; if (acc && tg.measureText(tl).width > availW) { lines.push(acc); acc = words[wi]; } else acc = tl; }
      if (acc) lines.push(acc);
      if (!lines.length) lines = [text];
      var lineH = 0;
      try { var rng = document.createRange(); rng.selectNodeContents(el); var lrs = rng.getClientRects(); if (lrs.length) lineH = lrs[0].height; } catch (e) {}
      var _fs = parseFloat(cs.fontSize) || 16;
      if (!lineH) lineH = parseFloat(cs.lineHeight) || Math.ceil(_fs * 1.35);
      if (lineH > _fs * 2.5) { var _lh = parseFloat(cs.lineHeight); lineH = (_lh > 0 && _lh <= _fs * 2.5) ? _lh : Math.ceil(_fs * 1.55); }
      var startY = padTop + Math.max(0, (rect.height - lines.length * lineH) / 2);
      var lineYs = lines.map(function (_, k) { return startY + k * lineH + lineH / 2; });
      tg.fillStyle = cs.color;
      for (var li = 0; li < lines.length; li++) tg.fillText(lines[li], cw / 2, lineYs[li]);

      // ---- sample fuel points + bounds ----
      var idata = tg.getImageData(0, 0, bw, bh).data;
      var step = Math.max(2, Math.round((small ? 2.6 : 1.9) * dpr));
      var pts = [], minX = 1e9, maxX = -1e9, minY = 1e9, maxY = -1e9;
      for (var qy = 0; qy < bh; qy += step) for (var qx = 0; qx < bw; qx += step) {
        if (idata[(qy * bw + qx) * 4 + 3] > 60) { var X = qx / dpr, Y = qy / dpr; pts.push({ x: X, y: Y }); if (X < minX) minX = X; if (X > maxX) maxX = X; if (Y < minY) minY = Y; if (Y > maxY) maxY = Y; }
      }
      if (!pts.length) { textCv = null; el.style.display = 'none'; reveal(); return; }
      var tH = Math.max(1, maxY - minY);

      // ---- ignition-time field: bottom-centre seed, climbs UP fast, sideways slower, ragged noise ----
      function h2(x, y) { var n = Math.sin(x * 127.1 + y * 311.7) * 43758.5453; return n - Math.floor(n); }
      function vn(x, y) { var xi = Math.floor(x), yi = Math.floor(y), xf = x - xi, yf = y - yi, u = xf * xf * (3 - 2 * xf), v = yf * yf * (3 - 2 * yf); var a = h2(xi, yi), b = h2(xi + 1, yi), c = h2(xi, yi + 1), d = h2(xi + 1, yi + 1); return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v; }
      function fbm(x, y) { return vn(x, y) * 0.6 + vn(x * 2.1, y * 2.1) * 0.28 + vn(x * 4.4, y * 4.4) * 0.12; }
      var seedX = (minX + maxX) / 2, seedY = maxY + rect.height * 0.14;
      function rawAt(x, y) { var above = Math.max(0, seedY - y), dx = Math.abs(x - seedX); var d = Math.sqrt(above * above + (dx * 1.55) * (dx * 1.55)); d += (fbm(x * 0.022, y * 0.022) - 0.5) * tH * 1.0 + (fbm(x * 0.09, y * 0.09) - 0.5) * tH * 0.4; return Math.max(0, d); }
      var maxRaw = 0;
      for (var pi = 0; pi < pts.length; pi++) { pts[pi].raw = rawAt(pts[pi].x, pts[pi].y); if (pts[pi].raw > maxRaw) maxRaw = pts[pi].raw; }
      var BURN = small ? 2.0 : 2.35, CONSUME = 0.30;
      for (var pj = 0; pj < pts.length; pj++) pts[pj].ig = (pts[pj].raw / maxRaw) * BURN + Math.random() * 0.05;
      pts.sort(function (a, b) { return a.ig - b.ig; });

      // ================= 2D PAPER LAYER =================
      var pcv = mkCanvas(6, ''); var ctx = pcv.getContext('2d'); ctx.scale(dpr, dpr);
      function buf() { var c = document.createElement('canvas'); c.width = bw; c.height = bh; var g = c.getContext('2d'); g.scale(dpr, dpr); return { c: c, g: g }; }
      function spr(color, soft) { var s = document.createElement('canvas'); s.width = s.height = 64; var g = s.getContext('2d'); var rg = g.createRadialGradient(32, 32, 0, 32, 32, 32); rg.addColorStop(0, color); rg.addColorStop(soft || 0.5, color); rg.addColorStop(1, 'rgba(0,0,0,0)'); g.fillStyle = rg; g.beginPath(); g.arc(32, 32, 32, 0, 6.2832); g.fill(); return s; }
      function tongue(color) { var w = 48, hh = 112, s = document.createElement('canvas'); s.width = w; s.height = hh; var g = s.getContext('2d'); var rg = g.createRadialGradient(w / 2, hh * 0.74, 0, w / 2, hh * 0.74, w * 0.6); rg.addColorStop(0, color); rg.addColorStop(0.45, color); rg.addColorStop(1, 'rgba(0,0,0,0)'); g.fillStyle = rg; g.fillRect(0, 0, w, hh); g.globalCompositeOperation = 'destination-in'; var vg = g.createLinearGradient(0, 0, 0, hh); vg.addColorStop(0, 'rgba(0,0,0,0)'); vg.addColorStop(0.28, 'rgba(0,0,0,0.45)'); vg.addColorStop(0.62, 'rgba(0,0,0,1)'); vg.addColorStop(1, 'rgba(0,0,0,0.75)'); g.fillStyle = vg; g.fillRect(0, 0, w, hh); return s; }
      var sEmber = spr('rgba(255,190,90,1)', 0.3), sSmoke = spr('rgba(22,15,12,1)', 0.4), sChar = spr('rgba(24,14,9,0.95)', 0.5), sHole = spr('rgba(255,255,255,1)', 0.6), sAsh = spr('rgba(18,13,10,1)', 0.45);
      var flameSpr = null; // built lazily only if WebGL fails
      var charBuf = buf(), burnBuf = buf();
      var embers = [], smoke = [], ash = [], tongues = [];

      // ================= WEBGL FIRE LAYER =================
      var fdpr = Math.min(dpr, small ? 1.0 : 1.5), fw = Math.round(cw * fdpr), fh = Math.round(ch * fdpr);
      var fcv = mkCanvas(7, 'screen');
      var gl = null; try { gl = fcv.getContext('webgl', { alpha: true, premultipliedAlpha: false, antialias: true }) || fcv.getContext('experimental-webgl'); } catch (e) {}
      var glOK = !!gl, uni = {}, texText = null, texIgn = null;
      if (glOK) { try {
        var VS = 'attribute vec2 a;varying vec2 vUV;void main(){vUV=vec2(a.x*0.5+0.5,1.0-(a.y*0.5+0.5));gl_Position=vec4(a,0.0,1.0);}';
        var FS = [
          'precision highp float;varying vec2 vUV;',
          'uniform sampler2D uText;uniform sampler2D uIgn;uniform float uTime;uniform float uBurn;uniform float uConsume;',
          'float hash(vec2 p){p=fract(p*vec2(123.34,456.21));p+=dot(p,p+45.32);return fract(p.x*p.y);}',
          'float vn(vec2 p){vec2 i=floor(p),f=fract(p);vec2 u=f*f*(3.0-2.0*f);float a=hash(i),b=hash(i+vec2(1.,0.)),c=hash(i+vec2(0.,1.)),d=hash(i+vec2(1.,1.));return mix(mix(a,b,u.x),mix(c,d,u.x),u.y);}',
          'float fbm(vec2 p){float s=0.0,a=0.55;for(int i=0;i<5;i++){s+=a*vn(p);p=p*2.03+11.7;a*=0.5;}return s;}',
          'float ignAt(vec2 uv){return texture2D(uIgn,uv).r*uBurn;}',
          'float fuelAt(vec2 uv){return texture2D(uText,uv).a;}',
          'void main(){',
          '  vec2 uv=vUV;float flame=0.0;',
          '  for(int i=1;i<=16;i++){',
          '    float h=float(i)/16.0;float dy=h*0.075;',
          '    float w=((fbm(vec2(uv.x*9.0,uv.y*6.0-uTime*2.6)+float(i)*4.3)-0.5)*0.10+(fbm(vec2(uv.x*21.0,uv.y*15.0-uTime*4.1))-0.5)*0.045)*h;',
          '    vec2 suv=vec2(uv.x+w,uv.y+dy);',
          '    float f=fuelAt(suv);float ph=uTime-ignAt(suv);',
          '    float burning=exp(-ph*ph/0.06);',
          '    float turb=0.5+1.05*fbm(vec2(uv.x*11.0,uv.y*11.0-uTime*3.4));',
          '    flame+=f*burning*(1.0-h*0.8)*turb;',
          '  }',
          '  flame=pow(clamp(flame/3.4,0.0,1.5),0.85);',
          '  float phF=uTime-ignAt(uv);float fuF=fuelAt(uv);',
          '  float core=fuF*exp(-phF*phF/0.022);flame=max(flame,core*1.25);',
          '  vec3 fire=vec3(0.0);',
          '  fire+=smoothstep(0.06,0.42,flame)*vec3(0.85,0.34,0.03);',
          '  fire+=smoothstep(0.34,0.8,flame)*vec3(0.62,0.72,0.06);',
          '  fire+=smoothstep(0.72,1.25,flame)*vec3(0.55,0.6,0.72);',
          '  float rim=fuF*exp(-pow((phF-uConsume)/0.055,2.0));',
          '  fire+=rim*vec3(0.9,0.42,0.05);',
          '  gl_FragColor=vec4(fire,1.0);',
          '}'].join('\n');
        function shdr(t, src) { var o = gl.createShader(t); gl.shaderSource(o, src); gl.compileShader(o); if (!gl.getShaderParameter(o, gl.COMPILE_STATUS)) throw gl.getShaderInfoLog(o); return o; }
        var prog = gl.createProgram(); gl.attachShader(prog, shdr(gl.VERTEX_SHADER, VS)); gl.attachShader(prog, shdr(gl.FRAGMENT_SHADER, FS)); gl.linkProgram(prog);
        if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw gl.getProgramInfoLog(prog);
        gl.useProgram(prog);
        var vb = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, vb); gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
        var la = gl.getAttribLocation(prog, 'a'); gl.enableVertexAttribArray(la); gl.vertexAttribPointer(la, 2, gl.FLOAT, false, 0, 0);
        ['uTime', 'uBurn', 'uConsume', 'uText', 'uIgn'].forEach(function (n) { uni[n] = gl.getUniformLocation(prog, n); });
        function mkTex(unit) { var t = gl.createTexture(); gl.activeTexture(gl.TEXTURE0 + unit); gl.bindTexture(gl.TEXTURE_2D, t); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR); return t; }
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
        // ignition texture (built as a canvas so FLIP_Y matches the text upload on every GPU)
        var IG = 2, iw = Math.max(1, Math.ceil(cw / IG)), ih = Math.max(1, Math.ceil(ch / IG));
        var ignArr = new Uint8ClampedArray(iw * ih * 4);
        for (var iy = 0; iy < ih; iy++) for (var ix = 0; ix < iw; ix++) { var g01 = Math.min(1, rawAt(ix * IG, iy * IG) / maxRaw); var o = (iy * iw + ix) * 4; var vv = g01 * 255 | 0; ignArr[o] = vv; ignArr[o + 1] = vv; ignArr[o + 2] = vv; ignArr[o + 3] = 255; }
        var ignCv = document.createElement('canvas'); ignCv.width = iw; ignCv.height = ih; ignCv.getContext('2d').putImageData(new ImageData(ignArr, iw, ih), 0, 0);
        texText = mkTex(0); gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, textCv);
        texIgn = mkTex(1); gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, ignCv);
        gl.uniform1i(uni.uText, 0); gl.uniform1i(uni.uIgn, 1);
        gl.uniform1f(uni.uBurn, BURN); gl.uniform1f(uni.uConsume, CONSUME);
        gl.viewport(0, 0, fw, fh); gl.clearColor(0, 0, 0, 1);
      } catch (e) { glOK = false; try { fcv.remove(); } catch (_) {} } }
      if (!glOK) flameSpr = [tongue('rgba(255,214,96,1)'), tongue('rgba(255,132,28,1)'), tongue('rgba(206,44,14,1)'), tongue('rgba(206,44,14,1)')];

      // ---- simulation ----
      var CAP = { e: small ? 120 : 240, s: small ? 90 : 170, a: small ? 150 : 300, t: small ? 200 : 420 };
      var T = 0, igIdx = 0, conIdx = 0, last = null;
      function stepIgnite() {
        while (igIdx < pts.length && pts[igIdx].ig <= T) { var p = pts[igIdx++];
          charBuf.g.drawImage(sChar, p.x - step * 1.7, p.y - step * 1.7, step * 3.4, step * 3.4);
          if (Math.random() < 0.05 && embers.length < CAP.e) embers.push({ x: p.x, y: p.y, vx: (Math.random() - 0.5) * 22, vy: -(60 + Math.random() * 95), life: 0, dur: 0.5 + Math.random() * 0.7 });
          if (Math.random() < 0.03 && smoke.length < CAP.s) smoke.push({ x: p.x, y: p.y, vx: (Math.random() - 0.5) * 6, vy: -(20 + Math.random() * 22), life: 0, dur: 1.2 + Math.random() * 1.3, sz: 7 + Math.random() * 10 });
          if (!glOK && Math.random() < 0.16 && tongues.length < CAP.t) tongues.push({ x: p.x + (Math.random() - 0.5) * 4, y: p.y, vx: (Math.random() - 0.5) * 10, vy: -(34 + Math.random() * 40), life: 0, dur: 0.5 + Math.random() * 0.5, sz: 3.1 + Math.random() * 3.4, sw: 0.6 + Math.random() * 0.9 });
        }
        while (conIdx < pts.length && pts[conIdx].ig + CONSUME <= T) { var q = pts[conIdx++];
          burnBuf.g.drawImage(sHole, q.x - step * 1.25, q.y - step * 1.25, step * 2.5, step * 2.5);
          if (Math.random() < 0.06 && ash.length < CAP.a) ash.push({ x: q.x, y: q.y, vx: (Math.random() - 0.5) * 20, vy: -(6 + Math.random() * 22), life: 0, dur: 1.6 + Math.random() * 1.6, sz: 1.4 + Math.random() * 2.2, rot: Math.random() * 6.28, vr: (Math.random() - 0.5) * 8 });
        }
      }
      function integrate(dt) { var tt = T;
        for (var i = embers.length - 1; i >= 0; i--) { var e = embers[i]; e.life += dt / e.dur; if (e.life >= 1) { embers.splice(i, 1); continue; } e.vy -= 30 * dt; e.vx += (fbm(e.x * 0.05 + tt, e.y * 0.05) - 0.5) * 260 * dt; e.vx *= 0.95; e.x += e.vx * dt; e.y += e.vy * dt; }
        for (var s = smoke.length - 1; s >= 0; s--) { var m = smoke[s]; m.life += dt / m.dur; if (m.life >= 1) { smoke.splice(s, 1); continue; } m.vy -= 11 * dt; m.vx += (fbm(m.x * 0.02 + tt * 0.4, m.y * 0.02) - 0.5) * 70 * dt; m.x += m.vx * dt; m.y += m.vy * dt; }
        for (var a = ash.length - 1; a >= 0; a--) { var k = ash[a]; k.life += dt / k.dur; if (k.life >= 1) { ash.splice(a, 1); continue; } k.vy += 46 * dt; k.vx += (fbm(k.x * 0.03 + tt * 0.5, k.y * 0.03) - 0.5) * 95 * dt; k.vx *= 0.985; k.vy *= 0.992; k.x += k.vx * dt; k.y += k.vy * dt; k.rot += k.vr * dt; }
        if (!glOK) for (var f = tongues.length - 1; f >= 0; f--) { var fl = tongues[f]; fl.life += dt / fl.dur; if (fl.life >= 1) { tongues.splice(f, 1); continue; } fl.vy -= 42 * dt; fl.vx += (fbm(fl.x * 0.03 + tt * 0.7, fl.y * 0.03) - 0.5) * 140 * fl.sw * dt; fl.vx *= 0.94; fl.x += fl.vx * dt; fl.y += fl.vy * dt; }
      }
      function draw2D() {
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0); ctx.clearRect(0, 0, cw, ch);
        ctx.globalCompositeOperation = 'source-over'; ctx.drawImage(textCv, 0, 0, cw, ch);
        ctx.globalCompositeOperation = 'source-atop'; ctx.drawImage(charBuf.c, 0, 0, cw, ch);
        ctx.globalCompositeOperation = 'destination-out'; ctx.drawImage(burnBuf.c, 0, 0, cw, ch);
        ctx.globalCompositeOperation = 'source-over';
        for (var a = 0; a < ash.length; a++) { var k = ash[a]; ctx.globalAlpha = (k.life < 0.7 ? 1 : (1 - k.life) / 0.3) * 0.9; ctx.drawImage(sAsh, k.x - k.sz, k.y - k.sz, k.sz * 2, k.sz * 2); }
        ctx.globalAlpha = 1;
        for (var s = 0; s < smoke.length; s++) { var m = smoke[s]; var ss = m.sz * (0.7 + m.life * 2.4); var sa = (m.life < 0.2 ? m.life / 0.2 : (1 - m.life) / 0.8) * 0.15; ctx.globalAlpha = sa; ctx.drawImage(sSmoke, m.x - ss, m.y - ss, ss * 2, ss * 2); }
        ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = 1;
        if (!glOK) for (var f = 0; f < tongues.length; f++) { var fl = tongues[f]; var sp = flameSpr[Math.min(3, (fl.life * 3.2) | 0)]; var fa = (fl.life < 0.18 ? fl.life / 0.18 : (1 - fl.life) / 0.82) * 0.55; var fwd = fl.sz * 2.0, fhd = fl.sz * (4.4 + fl.life * 1.6); ctx.globalAlpha = fa; ctx.drawImage(sp, fl.x - fwd / 2, fl.y - fhd * 0.74, fwd, fhd); }
        for (var e = 0; e < embers.length; e++) { var b = embers[e]; ctx.globalAlpha = (1 - b.life) * 0.9; var es = 1.3 + (1 - b.life) * 1.3; ctx.drawImage(sEmber, b.x - es * 3, b.y - es * 3, es * 6, es * 6); }
        ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
      }
      function drawGL() {
        if (!glOK) return;
        gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, texText);
        gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, texIgn);
        gl.uniform1f(uni.uTime, T); gl.clear(gl.COLOR_BUFFER_BIT); gl.drawArrays(gl.TRIANGLES, 0, 3);
      }
      function destroy() { try { pcv.remove(); } catch (e) {} if (glOK) { try { fcv.remove(); } catch (e) {} } }

      el.style.opacity = '0';
      function loop(ts) {
        if (last === null) last = ts;
        var dt = Math.min(0.05, (ts - last) / 1000) || 0.016; last = ts;
        T += dt; stepIgnite(); integrate(dt); draw2D(); drawGL();
        if (conIdx > pts.length * 0.82) reveal();
        if (T < BURN + 3.0 && (igIdx < pts.length || embers.length || ash.length || smoke.length || tongues.length)) requestAnimationFrame(loop);
        else { el.style.display = 'none'; destroy(); reveal(); }
      }
      requestAnimationFrame(loop);
    } catch (e) {
      try { el.style.display = 'none'; } catch (_) {}
      reveal();
    }
  }

  // Product card hover parallax: translates the image based on cursor position.
  // Pointer-only (skipped on touch). Uses transform so it composites cheaply.
  function initCardHoverParallax() {
    if (prefersReducedMotion) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    document.querySelectorAll('.product-card:not([data-hover-init])').forEach(function(card) {
      card.setAttribute('data-hover-init', '');
      var img = card.querySelector('.product-card__image-wrapper img');
      if (!img) return;

      var rect = null;
      var raf = null;
      var targetX = 0, targetY = 0, currentX = 0, currentY = 0;
      var maxOffset = 12; // pixels

      function loop() {
        // Smooth interpolation toward target position
        currentX += (targetX - currentX) * 0.15;
        currentY += (targetY - currentY) * 0.15;
        img.style.transform = 'translate3d(' + currentX.toFixed(2) + 'px, ' + currentY.toFixed(2) + 'px, 0) scale(1.05)';
        if (Math.abs(targetX - currentX) > 0.05 || Math.abs(targetY - currentY) > 0.05) {
          raf = requestAnimationFrame(loop);
        } else {
          raf = null;
        }
      }
      function start() { if (!raf) raf = requestAnimationFrame(loop); }

      card.addEventListener('mouseenter', function() {
        rect = card.getBoundingClientRect();
        img.style.willChange = 'transform';
        img.style.transition = 'none';
      });
      card.addEventListener('mousemove', function(e) {
        if (!rect) rect = card.getBoundingClientRect();
        var nx = (e.clientX - rect.left) / rect.width - 0.5;
        var ny = (e.clientY - rect.top) / rect.height - 0.5;
        targetX = nx * -maxOffset;
        targetY = ny * -maxOffset;
        start();
      });
      card.addEventListener('mouseleave', function() {
        targetX = 0; targetY = 0;
        img.style.transition = 'transform 0.5s var(--easing-smooth, ease-out)';
        img.style.transform = '';
        rect = null;
        if (raf) { cancelAnimationFrame(raf); raf = null; }
        currentX = 0; currentY = 0;
      });
    });
  }

  // Inline videos: play when in viewport, pause when out. Used for the
  // pill-shaped video pieces inside [data-split-words] story sections.
  // Videos inside the pinned brand-story section are managed by
  // initBrandStoryPin (via the scrub timeline) and skipped here.
  function initInlineVideos() {
    if (!gsapReady()) return;
    document.querySelectorAll('.inline-video:not([data-video-init])').forEach(function(video) {
      video.setAttribute('data-video-init', '');
      video.muted = true;
      video.playsInline = true;
      video.loop = true;
      video.preload = 'metadata';
      ScrollTrigger.create({
        trigger: video,
        start: 'top 95%',
        end: 'bottom 5%',
        onEnter: function() { var p = video.play(); if (p && p.catch) p.catch(function(){}); },
        onEnterBack: function() { var p = video.play(); if (p && p.catch) p.catch(function(){}); },
        onLeave: function() { video.pause(); },
        onLeaveBack: function() { video.pause(); }
      });
    });
  }

  // Auto-extract a dominant color from an image and expose it as --glow-color
  // CSS custom property on the parent element. Used by the hero branding-element
  // hover glow.
  function extractDominantColor(img) {
    return new Promise(function(resolve, reject) {
      var done = function() {
        try {
          var canvas = document.createElement('canvas');
          var size = 32; // tiny — fast and avoids tainted-canvas memory
          canvas.width = size; canvas.height = size;
          var ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, size, size);
          var data = ctx.getImageData(0, 0, size, size).data;
          var r = 0, g = 0, b = 0, count = 0;
          for (var i = 0; i < data.length; i += 4) {
            var alpha = data[i + 3];
            if (alpha < 200) continue; // skip transparent / semi pixels
            var rr = data[i], gg = data[i + 1], bb = data[i + 2];
            // Skip near-white & near-black
            var max = Math.max(rr, gg, bb), min = Math.min(rr, gg, bb);
            if (max > 245 && min > 245) continue;
            if (max < 20) continue;
            r += rr; g += gg; b += bb; count++;
          }
          if (!count) { reject(new Error('no opaque pixels')); return; }
          resolve('rgb(' + Math.round(r / count) + ',' + Math.round(g / count) + ',' + Math.round(b / count) + ')');
        } catch (e) { reject(e); }
      };
      if (img.complete && img.naturalWidth) done();
      else { img.addEventListener('load', done); img.addEventListener('error', reject); }
    });
  }

  function initBrandingElementGlow() {
    var wrap = document.querySelector('.hero__branding-element');
    if (!wrap || wrap.hasAttribute('data-glow-init')) return;
    wrap.setAttribute('data-glow-init', '');
    var img = wrap.querySelector('img');
    if (!img) return;

    // Need CORS to read pixels. Shopify CDN serves images with CORS headers.
    // Re-request the image with crossOrigin set so the canvas isn't tainted.
    var probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.onload = function() {
      extractDominantColor(probe)
        .then(function(color) { wrap.style.setProperty('--glow-color', color); })
        .catch(function() { wrap.style.setProperty('--glow-color', 'var(--color-primary)'); });
    };
    probe.onerror = function() { wrap.style.setProperty('--glow-color', 'var(--color-primary)'); };
    // The branding image may sit inside a <picture>, so the device-appropriate
    // URL is img.currentSrc (empty until the picture resolves) — fall back to src.
    var startProbe = function() { probe.src = img.currentSrc || img.src; };
    if (img.complete && img.naturalWidth) startProbe();
    else img.addEventListener('load', startProbe, { once: true });
  }

  function initAdvancedAnimations() {
    initParallax();
    initZoomScroll();
    initSplitWords();
    initSplitChars();
    initCounters();
    initBrandStory();
    initCardHoverParallax();
    initInlineVideos();
    initBrandingElementGlow();
  }
  if (document.readyState === 'complete') {
    initAdvancedAnimations();
  } else {
    window.addEventListener('load', initAdvancedAnimations);
  }
  setTimeout(initAdvancedAnimations, 2000);
  window.initAdvancedAnimations = initAdvancedAnimations;

  /* === Skeleton helpers === */
  window.skeletonProductCard = function() {
    return '' +
      '<div class="skeleton-card">' +
        '<div class="skeleton skeleton--image"></div>' +
        '<div class="skeleton-card__lines">' +
          '<div class="skeleton skeleton--text skeleton--text-sm"></div>' +
          '<div class="skeleton skeleton--text" style="width:80%"></div>' +
          '<div class="skeleton-card__price-row"><div class="skeleton"></div><div class="skeleton"></div></div>' +
          '<div class="skeleton skeleton--btn"></div>' +
        '</div>' +
      '</div>';
  };
  window.skeletonProductGrid = function(count) {
    count = count || 4;
    var html = '<div class="skeleton-grid">';
    for (var i = 0; i < count; i++) html += window.skeletonProductCard();
    html += '</div>';
    return html;
  };
  window.skeletonLines = function(count) {
    count = count || 3;
    var html = '<div style="display:flex;flex-direction:column;gap:10px">';
    for (var i = 0; i < count; i++) {
      html += '<div class="skeleton skeleton--text" style="width:' + (60 + Math.random() * 40) + '%"></div>';
    }
    html += '</div>';
    return html;
  };

  /* === Scroll-to-top with progress ring === */
  (function() {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'scroll-top';
    btn.setAttribute('aria-label', 'Scroll to top');
    btn.innerHTML =
      '<svg class="scroll-top__ring" viewBox="0 0 44 44">' +
        '<circle class="scroll-top__track" cx="22" cy="22" r="20" />' +
        '<circle class="scroll-top__progress" cx="22" cy="22" r="20" />' +
      '</svg>' +
      '<svg class="scroll-top__arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>';
    document.body.appendChild(btn);

    var progress = btn.querySelector('.scroll-top__progress');
    var circumference = 2 * Math.PI * 20;
    progress.style.strokeDasharray = circumference;
    progress.style.strokeDashoffset = circumference;

    var ticking = false;
    function update() {
      var doc = document.documentElement;
      var scrollTop = window.scrollY || doc.scrollTop;
      var max = (doc.scrollHeight - window.innerHeight) || 1;
      var ratio = Math.min(1, Math.max(0, scrollTop / max));
      progress.style.strokeDashoffset = circumference * (1 - ratio);
      btn.classList.toggle('is-visible', scrollTop > 400);
      ticking = false;
    }
    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();

    btn.addEventListener('click', function() {
      window.smoothScrollTo(0, { duration: 1.4 });
    });
  })();

  /* === Toast Notifications === */
  var toastContainer;
  function ensureToastContainer() {
    if (toastContainer) return toastContainer;
    toastContainer = document.createElement('div');
    toastContainer.className = 'toast-container';
    document.body.appendChild(toastContainer);
    return toastContainer;
  }
  window.showToast = function(message, type) {
    type = type || 'success';
    var c = ensureToastContainer();
    var t = document.createElement('div');
    t.className = 'toast toast--' + type;
    var icon = type === 'error'
      ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>'
      : type === 'info'
      ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>'
      : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>';
    t.innerHTML = '<span class="toast__icon">' + icon + '</span><span class="toast__msg">' + message + '</span>';
    c.appendChild(t);
    requestAnimationFrame(function() { t.classList.add('is-visible'); });
    setTimeout(function() {
      t.classList.remove('is-visible');
      setTimeout(function() { if (t.parentNode) t.parentNode.removeChild(t); }, 300);
    }, 2800);
  };

  /* === Custom Select (auto-enhances <select class="js-custom-select">) === */
  function buildCustomSelect(select) {
    if (select.dataset.cseInit) return;
    select.dataset.cseInit = '1';

    var wrap = document.createElement('div');
    wrap.className = 'custom-select';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'custom-select__btn';
    btn.setAttribute('aria-haspopup', 'listbox');
    btn.setAttribute('aria-expanded', 'false');

    var label = document.createElement('span');
    label.className = 'custom-select__label';
    var arrow = document.createElement('span');
    arrow.className = 'custom-select__arrow';
    arrow.innerHTML = '<svg viewBox="0 0 12 8" xmlns="http://www.w3.org/2000/svg"><path d="M1 1l5 5 5-5" stroke="currentColor" stroke-width="1.8" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    btn.appendChild(label);
    btn.appendChild(arrow);

    var list = document.createElement('ul');
    list.className = 'custom-select__list';
    list.setAttribute('role', 'listbox');
    // Let the list scroll natively instead of Lenis hijacking the wheel to the page
    list.setAttribute('data-lenis-prevent', '');

    function syncLabel() {
      var opt = select.options[select.selectedIndex];
      label.textContent = opt ? opt.textContent : '';
    }

    function rebuildList() {
      list.innerHTML = '';
      Array.prototype.forEach.call(select.options, function(opt, i) {
        if (opt.disabled && opt.value === '') return;   // hide a disabled placeholder ("Select …") from the list
        var li = document.createElement('li');
        li.className = 'custom-select__option' + (i === select.selectedIndex ? ' is-active' : '') + (opt.disabled ? ' is-disabled' : '');
        li.setAttribute('role', 'option');
        li.textContent = opt.textContent;
        if (!opt.disabled) {
          li.addEventListener('click', function() {
            select.selectedIndex = i;
            select.dispatchEvent(new Event('change', { bubbles: true }));
            syncLabel();
            Array.prototype.forEach.call(list.children, function(c) { c.classList.remove('is-active'); });
            li.classList.add('is-active');
            close();
          });
        }
        list.appendChild(li);
      });
    }

    function open() { wrap.classList.add('is-open'); btn.setAttribute('aria-expanded', 'true'); }
    function close() { wrap.classList.remove('is-open'); btn.setAttribute('aria-expanded', 'false'); }
    function toggle() { wrap.classList.contains('is-open') ? close() : open(); }

    btn.addEventListener('click', toggle);
    document.addEventListener('click', function(e) {
      if (!wrap.contains(e.target)) close();
    });

    select.parentNode.insertBefore(wrap, select);
    wrap.appendChild(select);
    wrap.appendChild(btn);
    wrap.appendChild(list);
    syncLabel();
    rebuildList();

    // Re-sync if value is changed externally
    select.addEventListener('change', function() {
      syncLabel();
      Array.prototype.forEach.call(list.children, function(c, i) {
        c.classList.toggle('is-active', i === select.selectedIndex);
      });
    });
  }

  function initCustomSelects() {
    document.querySelectorAll('select.js-custom-select').forEach(buildCustomSelect);
  }
  window.initCustomSelects = initCustomSelects;

  /* === Flip cards (shared) — same behaviour as the Brew Guide recipe cards ===
     Desktop (fine pointer): hover flips + a cursor-follow tilt with GSAP inertia.
     Touch: tap flips. Keyboard: focus + Enter/Space. The back face is a heavy SVG, so it is
     lazy-loaded on idle and on the first flip. Call with any root element containing
     [data-flip-card]; already-initialised cards are skipped, so it is safe to re-run. */
  window.initFlipCards = function(root) {
    root = root || document;
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-flip-card]'));
    if (!cards.length) return;
    var hasGsap = typeof gsap !== 'undefined';
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    function loadBack(card) {
      var img = card.querySelector('[data-flip-back]');
      if (img && !img.getAttribute('src') && img.dataset.src) img.setAttribute('src', img.dataset.src);
    }
    function preload() { cards.forEach(loadBack); }
    if ('requestIdleCallback' in window) requestIdleCallback(preload, { timeout: 4000 });
    else setTimeout(preload, 1500);

    cards.forEach(function(card) {
      if (card.__flipInit) return;
      card.__flipInit = true;
      var tilt = card.querySelector('[data-flip-tilt]');
      var inner = card.querySelector('[data-flip-inner]');
      var hasBack = !!card.querySelector('[data-flip-back]');
      if (!inner) return;
      var flipped = false;

      function flipTo(deg) {
        if (hasGsap && !reduce) gsap.to(inner, { rotationY: deg, duration: 0.85, ease: 'power3.inOut' });
        else {
          inner.style.transition = reduce ? 'none' : 'transform 0.7s cubic-bezier(0.22, 1, 0.36, 1)';
          inner.style.transform = 'rotateY(' + deg + 'deg)';
        }
      }
      function setFlipped(state) {
        if (!hasBack || state === flipped) return;
        flipped = state;
        card.setAttribute('aria-pressed', state ? 'true' : 'false');
        if (state) loadBack(card);
        flipTo(state ? 180 : 0);
      }
      if (hasBack) {
        card.addEventListener('click', function() { setFlipped(!flipped); });
        card.addEventListener('keydown', function(e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFlipped(!flipped); }
        });
      }
      // Desktop: hover flips + cursor-follow tilt
      if (canHover && hasGsap && !reduce && tilt) {
        var TILT_X = 11, TILT_Y = 14;
        var rotX = gsap.quickTo(tilt, 'rotationX', { duration: 0.6, ease: 'power3' });
        var rotY = gsap.quickTo(tilt, 'rotationY', { duration: 0.6, ease: 'power3' });
        card.addEventListener('mouseenter', function() {
          setFlipped(true);
          gsap.to(tilt, { scale: 1.045, duration: 0.5, ease: 'power2.out' });
        });
        card.addEventListener('mousemove', function(e) {
          var r = card.getBoundingClientRect();
          rotY(((e.clientX - r.left) / r.width - 0.5) * TILT_Y * 2);
          rotX(-((e.clientY - r.top) / r.height - 0.5) * TILT_X * 2);
        });
        card.addEventListener('mouseleave', function() {
          setFlipped(false);
          rotX(0); rotY(0);
          gsap.to(tilt, { scale: 1, duration: 0.6, ease: 'power2.out' });
        });
      }
    });
  };
  // Roast Story card lives inside a PDP tab panel — init once the DOM is ready.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() { window.initFlipCards(document.querySelector('[data-roast-story]')); });
  } else {
    window.initFlipCards(document.querySelector('[data-roast-story]'));
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCustomSelects);
  } else {
    initCustomSelects();
  }

  /* === Delayed Sticky Header === */
  (function() {
    var header = document.querySelector('.header');
    if (!header) return;

    // Insert spacer right after header to prevent layout jump when it goes fixed
    var spacer = document.createElement('div');
    spacer.className = 'header-spacer';
    header.parentNode.insertBefore(spacer, header.nextSibling);

    function getThreshold() {
      var hero = document.querySelector('.hero__banner');
      // Default: half the hero height. Fallback: 300px.
      return hero ? hero.offsetHeight / 2 : 300;
    }

    var threshold = getThreshold();
    var ticking = false;

    var hideTimer = null;

    function update() {
      var y = window.scrollY || window.pageYOffset;
      if (y > threshold) {
        if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
        if (!header.classList.contains('is-sticky')) {
          spacer.style.height = header.offsetHeight + 'px';
          spacer.classList.add('is-active');
          header.classList.add('is-sticky');
          // Defer is-visible to the next frame (after the next frame's
          // style recalc) so the browser truly paints translateY(-100%)
          // first, then animates to 0 in the following frame.
          requestAnimationFrame(function() {
            requestAnimationFrame(function() {
              header.classList.add('is-visible');
            });
          });
        }
      } else {
        if (header.classList.contains('is-visible')) {
          header.classList.remove('is-visible');
          // Wait for slide-up transition before removing is-sticky/spacer
          hideTimer = setTimeout(function() {
            header.classList.remove('is-sticky');
            spacer.classList.remove('is-active');
            spacer.style.height = '';
            hideTimer = null;
          }, 450);
        }
        // Safety: if stuck in is-sticky without is-visible (e.g. fast scroll race),
        // clean up immediately so the header doesn't block the viewport invisibly.
        if (header.classList.contains('is-sticky') && !header.classList.contains('is-visible') && !hideTimer && y <= 0) {
          header.classList.remove('is-sticky');
          spacer.classList.remove('is-active');
          spacer.style.height = '';
        }
      }
      ticking = false;
    }

    window.addEventListener('scroll', function() {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });

    window.addEventListener('resize', function() {
      threshold = getThreshold();
      if (header.classList.contains('is-sticky')) {
        spacer.style.height = header.offsetHeight + 'px';
      }
    });
  })();

  /* === Mobile Menu Toggle === */
  const menuToggle = document.querySelector('.header__menu-toggle');
  const header = document.querySelector('.header');

  if (menuToggle && header) {
    menuToggle.addEventListener('click', function(e) {
      e.stopPropagation();
      header.classList.toggle('menu-open');
      const isOpen = header.classList.contains('menu-open');
      menuToggle.setAttribute('aria-expanded', isOpen);
    });

    // Close menu when clicking outside
    document.addEventListener('click', function(e) {
      if (header.classList.contains('menu-open') && !header.contains(e.target)) {
        header.classList.remove('menu-open');
        menuToggle.setAttribute('aria-expanded', 'false');
      }
    });
  }

  /* === Accordion === */
  document.querySelectorAll('.accordion__header').forEach(function(button) {
    button.addEventListener('click', function() {
      const item = this.closest('.accordion__item');
      const isOpen = item.classList.contains('is-open');

      // Close all items — animate each open body down to 0 from its real height
      document.querySelectorAll('.accordion__item').forEach(function(i) {
        i.classList.remove('is-open');
        var b = i.querySelector('.accordion__body');
        if (b) b.style.maxHeight = '0px';
      });

      // Open clicked item to its actual content height (so the transition is tight — no dead time)
      if (!isOpen) {
        item.classList.add('is-open');
        var body = item.querySelector('.accordion__body');
        if (body) body.style.maxHeight = body.scrollHeight + 'px';
      }
    });
  });

  /* === Product Card Wishlist Toggle === */
  var wishlist = JSON.parse(localStorage.getItem('smglr-wishlist') || '[]');

  // Initialize wishlist count
  document.querySelectorAll('[data-wishlist-count]').forEach(function(el) {
    el.textContent = wishlist.length;
  });

  // Mark already-wishlisted products
  document.querySelectorAll('.product-card__wishlist').forEach(function(btn) {
    var handle = btn.dataset.productHandle;
    if (handle && wishlist.indexOf(handle) > -1) {
      btn.classList.add('is-active');
    }
  });

  document.addEventListener('click', function(e) {
    var btn = e.target.closest('.product-card__wishlist');
    if (!btn) return;
    e.preventDefault();

    var handle = btn.dataset.productHandle;
    if (!handle) { btn.classList.toggle('is-active'); return; }

    var wl = JSON.parse(localStorage.getItem('smglr-wishlist') || '[]');
    var idx = wl.indexOf(handle);
    if (idx > -1) {
      wl.splice(idx, 1);
      btn.classList.remove('is-active');
      window.showToast('Removed from wishlist', 'info');
    } else {
      wl.push(handle);
      btn.classList.add('is-active');
      window.showToast('Added to wishlist', 'success');
    }
    localStorage.setItem('smglr-wishlist', JSON.stringify(wl));
    document.querySelectorAll('[data-wishlist-count]').forEach(function(el) {
      el.textContent = wl.length;
    });
  });

  /* === Collection Tab Switching === */
  document.querySelectorAll('[data-collection-tab]').forEach(function(tab) {
    tab.addEventListener('click', function(e) {
      e.preventDefault();
      const target = this.dataset.collectionTab;

      // Update active tab
      document.querySelectorAll('[data-collection-tab]').forEach(function(t) {
        t.classList.remove('btn--primary');
        t.classList.add('btn--secondary');
      });
      this.classList.remove('btn--secondary');
      this.classList.add('btn--primary');

      // Show/hide product grids
      document.querySelectorAll('[data-collection-content]').forEach(function(content) {
        if (content.dataset.collectionContent === target || target === 'all') {
          content.style.display = '';
        } else {
          content.style.display = 'none';
        }
      });
    });
  });

  /* === Smooth Scroll for Anchor Links (uses Lenis if present) === */
  document.addEventListener('click', function(e) {
    var anchor = e.target.closest('a[href^="#"]');
    if (!anchor) return;
    var href = anchor.getAttribute('href');
    if (!href || href === '#') return;
    var target = document.querySelector(href);
    if (target) {
      e.preventDefault();
      window.smoothScrollTo(target, { duration: 1.4, offset: -20 });
    }
  });

  /* === Newsletter Form === */
  const newsletterForm = document.querySelector('.newsletter-section__form');
  if (newsletterForm) {
    newsletterForm.addEventListener('submit', function(e) {
      // Shopify handles this via the customer form, but we add UX feedback
      const button = this.querySelector('button[type="submit"]');
      if (button) {
        button.textContent = 'Subscribing...';
        button.disabled = true;
      }
    });
  }

  /* === Lazy Loading Images === */
  if ('IntersectionObserver' in window) {
    const lazyImages = document.querySelectorAll('img[data-src]');
    const imageObserver = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          const img = entry.target;
          img.src = img.dataset.src;
          if (img.dataset.srcset) {
            img.srcset = img.dataset.srcset;
          }
          img.classList.add('loaded');
          imageObserver.unobserve(img);
        }
      });
    }, { rootMargin: '200px 0px' });

    lazyImages.forEach(function(img) {
      imageObserver.observe(img);
    });
  }

  /* === Announcement bar marquee ===
     Liquid emits a single half; repeat it until it overruns the bar, then mirror it so the
     -50% translate loops seamlessly. Duration comes from the measured width so the speed
     stays constant (px/sec) no matter how long the copy is. */
  function initAnnouncementMarquee() {
    document.querySelectorAll('[data-marquee-track]').forEach(function(track) {
      if (track.__marqueeInit) return;
      var half = track.querySelector('[data-marquee-half]');
      var host = track.parentNode;
      if (!half || !host) return;

      var base = half.innerHTML;
      var guard = 0;
      // fill past the bar's width so a moderate resize can't open a gap
      while (half.offsetWidth < host.offsetWidth * 1.5 && guard++ < 24) half.innerHTML += base;

      var mirror = half.cloneNode(true);
      mirror.setAttribute('aria-hidden', 'true');
      mirror.removeAttribute('data-marquee-half');
      track.appendChild(mirror);

      var speed = parseFloat(track.getAttribute('data-marquee-speed')) || 60;
      track.style.setProperty('--marquee-duration', (half.offsetWidth / speed).toFixed(2) + 's');
      track.setAttribute('data-marquee-ready', '');
      track.__marqueeInit = true;
    });
  }
  initAnnouncementMarquee();
  window.initAnnouncementMarquee = initAnnouncementMarquee;

  /* === Pause always-on animations while off-screen (frees the compositor during interactions like the FAQ accordion) === */
  if ('IntersectionObserver' in window) {
    var pausables = document.querySelectorAll('.footer-marquee, .faq-section__coffee-image, .announcement-bar--marquee');
    if (pausables.length) {
      var pauseObserver = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          entry.target.classList.toggle('is-anim-paused', !entry.isIntersecting);
        });
      }, { rootMargin: '150px 0px' });
      pausables.forEach(function(el) { pauseObserver.observe(el); });
    }
  }

  /* === Add to Cart (AJAX) + "Go to cart" toggle === */
  var GO_TO_CART_LABEL = 'Go to cart';
  var ADD_TO_CART_LABEL = 'Add to cart';
  var cartVariantIds = new Set();
  var cartProductIds = new Set();

  function markBtnInCart(btn) {
    btn.classList.add('is-in-cart');
    btn.dataset.originalLabel = btn.dataset.originalLabel || btn.textContent.trim();
    btn.textContent = GO_TO_CART_LABEL;
  }
  function unmarkBtnInCart(btn) {
    btn.classList.remove('is-in-cart');
    if (btn.dataset.originalLabel) btn.textContent = btn.dataset.originalLabel;
  }
  function refreshAllAddBtns() {
    document.querySelectorAll('[data-add-to-cart][data-variant-id]').forEach(function(btn) {
      // A product-card add button represents the whole product, so it flips to "Go to cart" when ANY
      // of the product's variants is in the cart (multi-variant included — the shopper may have added a
      // non-default variant via the modal). Falls back to the specific variant id if no product id.
      var pid = parseInt(btn.dataset.productId, 10);
      var vid = parseInt(btn.dataset.variantId, 10);
      if ((pid && cartProductIds.has(pid)) || cartVariantIds.has(vid)) markBtnInCart(btn);
      else unmarkBtnInCart(btn);
    });
    // Product page main button (uses data-product-add-btn + hidden input)
    document.querySelectorAll('[data-product-add-btn]').forEach(function(btn) {
      var section = btn.closest('[data-product-section]');
      if (!section) return;
      var hidden = section.querySelector('[data-product-variant-id]');
      if (!hidden) return;
      var id = parseInt(hidden.value, 10);
      if (cartVariantIds.has(id)) markBtnInCart(btn);
      else unmarkBtnInCart(btn);
    });
  }
  function refreshCartState() {
    return fetch('/cart.js')
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        cartVariantIds = new Set((cart.items || []).map(function(i) { return i.variant_id; }));
        cartProductIds = new Set((cart.items || []).map(function(i) { return i.product_id; }));
        document.querySelectorAll('.header__count[data-cart-count]').forEach(function(el) {
          el.textContent = cart.item_count;
        });
        var bar = document.querySelector('[data-mobile-cart-bar]');
        if (bar) {
          if (cart.item_count > 0) {
            bar.removeAttribute('hidden');
            var fmt = '₹ ' + (cart.total_price/100).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2});
            var c = bar.querySelector('[data-mobile-cart-count]');
            var ct = bar.querySelector('[data-mobile-cart-count-text]');
            var tt = bar.querySelector('[data-mobile-cart-total]');
            if (c) c.textContent = cart.item_count;
            if (ct) ct.textContent = cart.item_count + ' ' + (cart.item_count === 1 ? 'item' : 'items');
            if (tt) tt.textContent = fmt;
          } else {
            bar.setAttribute('hidden', '');
          }
        }
        refreshAllAddBtns();
        return cart;
      });
  }
  // Expose so other closures (quick-add modal, product page) can refresh the header/mobile cart
  // count + "in cart" button states after an AJAX add. Without this, adds outside this scope
  // left the header count stale until a page reload.
  window.refreshCartState = refreshCartState;
  // A "Buy now" isolates its checkout by snapshotting + emptying the cart; this re-adds the snapshot when
  // the shopper returns (completed or abandoned) so their other items aren't lost. Robust: adds items ONE
  // at a time (a single now-unavailable line can't discard the rest), drops each line from the snapshot
  // only once it's resolved (added, or gone / 4xx), and KEEPS a network-failed line for the next page load
  // to retry — persisting after every item so an interrupted restore never double-adds.
  // True while a snapshot restore is mid-flight, so a concurrent Buy now won't clear the cart out from under it
  // (the Buy now handler degrades to a non-isolated add+checkout while this is set — see restoreBusy there).
  var restoreBusy = false;
  function restoreBuyNowSnapshot() {
    var KEY = 'smglr-buynow-restore', raw;
    try { raw = localStorage.getItem(KEY); } catch (e) { return; }
    if (!raw) return;
    var remaining;
    try { remaining = JSON.parse(raw); } catch (e) { try { localStorage.removeItem(KEY); } catch (x) {} return; }
    if (!remaining || !remaining.length) { try { localStorage.removeItem(KEY); } catch (x) {} return; }
    var addedAny = false;
    restoreBusy = true;
    function persist() { try { if (remaining.length) localStorage.setItem(KEY, JSON.stringify(remaining)); else localStorage.removeItem(KEY); } catch (e) {} }
    function step() {
      if (!remaining.length) { restoreBusy = false; if (addedAny) refreshCartState(); return; }
      var it = remaining[0];
      var itBody = { id: it.id, quantity: it.quantity };
      if (it.properties) itBody.properties = it.properties;   // preserve combo picks / notes across the buy-now snapshot
      fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(itBody) })
        .then(function (r) { if (r.ok) addedAny = true; remaining.shift(); persist(); step(); }) // added, or unavailable(4xx) -> done
        .catch(function () { persist(); restoreBusy = false; }); // network error -> stop; the surviving lines retry on the next page load
    }
    step();
  }
  window.__smglrRestoreCart = restoreBuyNowSnapshot;
  restoreBuyNowSnapshot();
  refreshCartState();
  // Re-check on variant change in product page (the section's update() dispatches no event,
  // so we observe the hidden input via MutationObserver as a safety net)
  document.addEventListener('change', function(e) {
    if (e.target.matches && e.target.matches('[data-product-variant-id]')) refreshAllAddBtns();
  });
  // Hook into product page custom variant updates: poll briefly after a click on pills
  document.addEventListener('click', function(e) {
    if (e.target.closest('.product-page__pill')) {
      setTimeout(refreshAllAddBtns, 50);
    }
  });

  document.addEventListener('click', function(e) {
    var addToCartBtn = e.target.closest('[data-add-to-cart], [data-product-add-btn]');
    if (!addToCartBtn) return;

    // If marked as "in cart", navigate to /cart instead of adding
    if (addToCartBtn.classList.contains('is-in-cart')) {
      e.preventDefault();
      window.location.href = '/cart';
      return;
    }

    // Only handle [data-add-to-cart] via AJAX (product card quick-add).
    // Product page button uses native form submit; we just refresh state after submit.
    if (!addToCartBtn.matches('[data-add-to-cart]')) return;

    // Multi-variant product (e.g. grind/size): don't add the first variant blindly — open the quick-add
    // modal so the shopper picks a variant first (its "Grind For" dropdown + add/buy). Sourced from the
    // card's quick-add button so the modal gets the full product + metafield data.
    if (addToCartBtn.hasAttribute('data-needs-options')) {
      e.preventDefault();
      var acCard = addToCartBtn.closest('[data-product-card]');
      var acQa = acCard && acCard.querySelector('[data-quick-add]');
      if (acQa && typeof window.openQuickAdd === 'function') window.openQuickAdd(acQa.getAttribute('data-quick-add'), acQa);
      return;
    }

    e.preventDefault();
    var variantId = addToCartBtn.dataset.variantId;
    if (!variantId) return;

    var originalText = addToCartBtn.textContent;
    addToCartBtn.textContent = 'Adding...';
    addToCartBtn.disabled = true;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId), quantity: 1 }] })
    })
    .then(function(response) { return response.json(); })
    .then(function() {
      addToCartBtn.textContent = originalText;
      addToCartBtn.disabled = false;
      // No "Added to cart" toast — the drawer opening is the confirmation. Failures still toast.
      if (window.revealCartAfterAdd) window.revealCartAfterAdd();
      return refreshCartState();
    })
    .catch(function() {
      addToCartBtn.textContent = originalText;
      addToCartBtn.disabled = false;
      window.showToast('Could not add to cart', 'error');
    });
  });

  /* =======================================================
     PHASE 2/3: focus trap, modals, drawers, search, recents,
     compare, back-in-stock, lightbox, async error helpers
     ======================================================= */

  // --- Focus trap utility ---
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  window.trapFocus = function(container) {
    if (!container) return function() {};
    var prevFocus = document.activeElement;
    // Re-query LIVE on each Tab so controls injected after the trap is set (skeleton -> real cart/modal
    // content) are reachable — a snapshot taken at open time would freeze the trap to the skeleton.
    function focusables() { return container.querySelectorAll(FOCUSABLE); }
    var initial = focusables();
    if (initial.length) initial[0].focus();
    function onKey(e) {
      if (e.key !== 'Tab') { if (e.key === 'Escape') container.dispatchEvent(new CustomEvent('trap:escape')); return; }
      var f = focusables();
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener('keydown', onKey);
    document.body.classList.add('focus-trap-active');
    return function release() {
      container.removeEventListener('keydown', onKey);
      document.body.classList.remove('focus-trap-active');
      if (prevFocus && prevFocus.focus) prevFocus.focus();
    };
  };

  // --- Async error surface (for any async load failure) ---
  window.renderAsyncError = function(container, message, retryFn) {
    if (!container) return;
    var msg = message || 'Something went wrong.';
    container.innerHTML = '<div class="async-error">' + msg +
      (retryFn ? '<button type="button" class="async-error__retry" data-async-retry>Retry</button>' : '') +
    '</div>';
    if (retryFn) {
      var btn = container.querySelector('[data-async-retry]');
      if (btn) btn.addEventListener('click', retryFn);
    }
  };

  // --- Fetch with timeout helper ---
  window.fetchWithTimeout = function(url, opts, timeoutMs) {
    opts = opts || {}; timeoutMs = timeoutMs || 10000;
    var controller = new AbortController();
    var timer = setTimeout(function() { controller.abort(); }, timeoutMs);
    opts.signal = controller.signal;
    return fetch(url, opts).finally(function() { clearTimeout(timer); });
  };

  // --- Lightbox / product gallery overlay ---
  // Single image (any [data-lightbox]) OR a full gallery when the trigger sits inside a
  // [data-gallery-root]: a thumbnail rail on the left (fills column 1, then wraps into column 2…)
  // beside a large stage. Thumbs are read straight from the PDP rail, so it is always in sync.
  (function() {
    var overlay = null, items = [], index = 0, releaseTrap = null;
    function ensure() {
      if (overlay) return overlay;
      overlay = document.createElement('div');
      overlay.className = 'lightbox';
      overlay.innerHTML =
        '<button type="button" class="lightbox__close" aria-label="Close"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>' +
        '<div class="lightbox__gallery">' +
          '<div class="lightbox__thumbs" data-lightbox-thumbs role="listbox" aria-label="Product images"></div>' +
          '<div class="lightbox__stage">' +
            '<button type="button" class="lightbox__nav lightbox__nav--prev" data-lightbox-prev aria-label="Previous image"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg></button>' +
            '<img class="lightbox__image" alt="">' +
            '<button type="button" class="lightbox__nav lightbox__nav--next" data-lightbox-next aria-label="Next image"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></button>' +
            '<p class="lightbox__counter" data-lightbox-counter></p>' +
          '</div>' +
        '</div>';
      document.body.appendChild(overlay);
      overlay.querySelector('.lightbox__close').addEventListener('click', close);
      overlay.addEventListener('click', function(e) { if (e.target === overlay || e.target.classList.contains('lightbox__gallery') || e.target.classList.contains('lightbox__stage')) close(); });
      overlay.querySelector('[data-lightbox-prev]').addEventListener('click', function() { show(index - 1); });
      overlay.querySelector('[data-lightbox-next]').addEventListener('click', function() { show(index + 1); });
      overlay.querySelector('[data-lightbox-thumbs]').addEventListener('click', function(e) {
        var b = e.target.closest('[data-lightbox-thumb]');
        if (b) show(parseInt(b.getAttribute('data-lightbox-thumb'), 10) || 0);
      });
      document.addEventListener('keydown', function(e) {
        if (!overlay.classList.contains('is-open')) return;
        if (e.key === 'Escape') close();
        else if (e.key === 'ArrowLeft' && items.length > 1) { e.preventDefault(); show(index - 1); }
        else if (e.key === 'ArrowRight' && items.length > 1) { e.preventDefault(); show(index + 1); }
      });
      return overlay;
    }
    function show(i) {
      if (!items.length) return;
      index = (i + items.length) % items.length;          // wrap around
      var img = overlay.querySelector('.lightbox__image');
      img.src = items[index].src;
      img.alt = items[index].alt || '';
      var rail = overlay.querySelector('[data-lightbox-thumbs]');
      rail.querySelectorAll('[data-lightbox-thumb]').forEach(function(b) {
        var on = parseInt(b.getAttribute('data-lightbox-thumb'), 10) === index;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
        if (on && b.scrollIntoView) { try { b.scrollIntoView({ block: 'nearest', inline: 'nearest' }); } catch (e) {} }
      });
      var c = overlay.querySelector('[data-lightbox-counter]');
      if (c) c.textContent = items.length > 1 ? (index + 1) + ' / ' + items.length : '';
    }
    function renderThumbs() {
      var rail = overlay.querySelector('[data-lightbox-thumbs]');
      if (items.length < 2) { rail.hidden = true; rail.innerHTML = ''; return; }
      rail.hidden = false;
      var html = '';
      items.forEach(function(it, i) {
        html += '<button type="button" class="lightbox__thumb" data-lightbox-thumb="' + i + '" role="option" aria-selected="false" aria-label="Image ' + (i + 1) + '">' +
                  '<img src="' + (it.thumb || it.src) + '" alt="" loading="lazy" decoding="async">' +
                '</button>';
      });
      rail.innerHTML = html;
    }
    function openWith(list, start) {
      ensure();
      items = list || [];
      if (!items.length) return;
      overlay.classList.toggle('lightbox--gallery', items.length > 1);
      renderThumbs();
      show(start || 0);
      overlay.classList.add('is-open');
      if (window.lenis) window.lenis.stop();
      document.body.classList.add('lightbox-open');
      releaseTrap = window.trapFocus(overlay);
    }
    function open(src, alt) { openWith([{ src: src, alt: alt || '' }], 0); }   // single-image (back-compat)
    function close() {
      if (!overlay) return;
      overlay.classList.remove('is-open');
      if (window.lenis) window.lenis.start();
      document.body.classList.remove('lightbox-open');
      if (releaseTrap) { releaseTrap(); releaseTrap = null; }
    }
    // Build the image list from a PDP thumbnail rail (single source of truth — every image is
    // rendered there, even the ones hidden on desktop/tablet).
    function itemsFromRoot(root) {
      var out = [];
      if (!root) return out;
      root.querySelectorAll('[data-thumb]').forEach(function(t) {
        var im = t.querySelector('img');
        out.push({ src: t.getAttribute('data-thumb'), thumb: (im && im.getAttribute('src')) || t.getAttribute('data-thumb'), alt: (im && im.alt) || '' });
      });
      return out;
    }
    window.openLightbox = open;
    window.openGallery = openWith;
    document.addEventListener('click', function(e) {
      // "+N" badge (or its a11y twin): open the gallery at the first hidden image
      var more = e.target.closest('[data-gallery-more]');
      if (more) {
        e.preventDefault();
        e.stopPropagation();
        var mRoot = more.closest('[data-gallery-root]');
        var mItems = itemsFromRoot(mRoot);
        if (mItems.length) { openWith(mItems, parseInt(more.getAttribute('data-gallery-more'), 10) || 0); return; }
      }
      var trigger = e.target.closest('[data-lightbox]');
      if (!trigger) return;
      e.preventDefault();
      var img = trigger.tagName === 'IMG' ? trigger : trigger.querySelector('img');
      var src = trigger.getAttribute('data-lightbox') || (img && img.src);
      var root = trigger.closest('[data-gallery-root]');
      var list = itemsFromRoot(root);
      if (list.length > 1) {
        // open on whatever the shopper is currently looking at
        var active = root.querySelector('[data-thumb].is-active');
        var start = active ? (parseInt(active.getAttribute('data-index'), 10) || 0) : 0;
        var cur = img && img.currentSrc ? img.currentSrc : (img && img.src);
        if (cur) { for (var i = 0; i < list.length; i++) { if (list[i].src === cur) { start = i; break; } } }
        openWith(list, start);
        return;
      }
      if (src) open(src, img && img.alt);
    });
  })();

  // --- Cart recommendation engine (shared by the drawer bar + the cart page row) ---
  // Fetches related products via the Section Rendering API (seeded by a cart product, with the
  // section's own collection fallback), wires the add buttons, and plays the GSAP fly-to-cart.
  (function() {
    var listCache = {}; // seed -> cards HTML (fetched once per seed)
    function fetchList(seed, limit) {
      if (!seed) return Promise.resolve('');
      var key = seed + ':' + (limit || 10);
      if (listCache[key]) return Promise.resolve(listCache[key]);
      var base = window.routesProductRecs || '/recommendations/products';
      var url = base + '?section_id=cart-recommendations&intent=related&limit=' +
        (limit || 10) + '&product_id=' + encodeURIComponent(seed);
      return fetch(url)
        .then(function(r) { return r.ok ? r.text() : ''; })
        .then(function(html) {
          if (!html) return '';
          var list;
          try { list = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-recs-list]'); } catch (e) {}
          var out = list ? list.innerHTML : '';
          listCache[key] = out;
          return out;
        })
        .catch(function() { return ''; });
    }
    // Hide rec cards whose product is already in the cart; show the rest. Returns visible count.
    // Clears any leftover fly/collapse inline styles so a card that reappears (item removed) is pristine.
    function filterCards(container, cart) {
      if (!container) return 0;
      var inCart = {};
      ((cart && cart.items) || []).forEach(function(i) { inCart[i.product_id] = true; });
      var visible = 0;
      container.querySelectorAll('.cart-rec').forEach(function(card) {
        var pid = parseInt(card.getAttribute('data-product-id'), 10);
        if (inCart[pid]) {
          card.hidden = true;
        } else {
          if (card.hidden) {
            // Reappearing (its product was removed from the cart): wipe the fly/collapse styles and
            // re-arm the add button so it can be added again.
            card.hidden = false;
            card.style.opacity = ''; card.style.transform = '';
            card.classList.remove('is-adding');
            var b = card.querySelector('[data-cart-rec-add]');
            if (b) b.disabled = false;
          }
          visible++;
        }
      });
      return visible;
    }
    function popCartIcon() {
      var count = document.querySelector('.header__count[data-cart-count]');
      if (count) { count.classList.remove('cart-count-pop'); void count.offsetWidth; count.classList.add('cart-count-pop'); }
      var icon = document.querySelector('[data-cart-drawer-toggle]');
      if (window.gsap && icon) gsap.fromTo(icon, { scale: 1 }, { scale: 1.22, duration: 0.16, yoyo: true, repeat: 1, ease: 'power2.out' });
    }
    // A curved (quadratic-bezier) flight of an image clone from the rec card to the header cart icon.
    function flyToCart(sourceImg, targetEl, done) {
      var target = targetEl || document.querySelector('[data-cart-drawer-toggle]') || document.querySelector('.header__count');
      if (!window.gsap || !sourceImg || !target || (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) {
        popCartIcon(); if (done) done(); return;
      }
      var s = sourceImg.getBoundingClientRect();
      var t = target.getBoundingClientRect();
      var clone = document.createElement('img');
      clone.src = sourceImg.currentSrc || sourceImg.src;
      clone.className = 'cart-rec-fly';
      clone.style.width = s.width + 'px';
      clone.style.height = s.height + 'px';
      document.body.appendChild(clone);
      var sx = s.left, sy = s.top;
      var ex = t.left + t.width / 2 - s.width * 0.1;
      var ey = t.top + t.height / 2 - s.height * 0.1;
      var cx = (sx + ex) / 2 + 60;                 // control point: pull right + up for a graceful arc
      var cy = Math.min(sy, ey) - 150;
      var st = { t: 0 };
      gsap.set(clone, { x: sx, y: sy, scale: 1, opacity: 1, rotation: 0, transformOrigin: '50% 50%' });
      gsap.to(st, {
        t: 1, duration: 0.82, ease: 'power2.inOut',
        onUpdate: function() {
          var p = st.t, m = 1 - p;
          var x = m * m * sx + 2 * m * p * cx + p * p * ex;
          var y = m * m * sy + 2 * m * p * cy + p * p * ey;
          gsap.set(clone, { x: x, y: y, scale: 1 - 0.82 * p, rotation: 130 * p, opacity: p > 0.82 ? Math.max(0, 1 - (p - 0.82) / 0.18) : 1 });
        },
        onComplete: function() { clone.remove(); popCartIcon(); if (done) done(); }
      });
    }
    function collapseCard(card) {
      if (!card) return;
      if (!window.gsap) { card.hidden = true; return; }
      gsap.to(card, { opacity: 0, scale: 0.82, duration: 0.32, ease: 'power2.in', onComplete: function() {
        card.hidden = true; gsap.set(card, { clearProps: 'opacity,transform' });
      }});
    }
    // Wire every not-yet-wired add button inside `container`. opts.onAdd(cart) fires after the
    // add + cart refresh so the caller can repaint its cart view and re-filter the rec list.
    function wireAdds(container, opts) {
      if (!container) return;
      opts = opts || {};
      container.querySelectorAll('[data-cart-rec-add]').forEach(function(btn) {
        if (btn.__recWired) return;
        btn.__recWired = true;
        btn.addEventListener('click', function() {
          if (btn.disabled) return;
          // Combo product: can't one-click add (it needs selections) — open the quick-add modal instead.
          var comboData = btn.getAttribute('data-combo');
          if (comboData && window.openQuickAdd) {
            var rc = btn.closest('.cart-rec');
            var rh = rc && rc.getAttribute('data-handle');
            if (rh) { window.openQuickAdd(rh, btn); return; }
          }
          btn.disabled = true;
          var card = btn.closest('.cart-rec');
          if (card) card.classList.add('is-adding');
          var variantId = btn.getAttribute('data-variant-id');
          var img = card && card.querySelector('.cart-rec__img');
          flyToCart(img, opts.target || null);
          window.fetchWithTimeout('/cart/add.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: 1 })
          }, 8000)
            .then(function(r) { if (!r.ok) throw new Error('add'); return r.json(); })
            .then(function() {
              collapseCard(card);
              // No toast, and deliberately no drawer open — this add happens INSIDE the
              // already-open drawer. The card collapsing and the cart re-rendering are the
              // confirmation. The failure path below still toasts.
              return window.refreshCartState ? window.refreshCartState() : fetch('/cart.js').then(function(r) { return r.json(); });
            })
            .then(function(cart) { if (opts.onAdd) opts.onAdd(cart); })
            .catch(function() {
              btn.disabled = false;
              if (card) card.classList.remove('is-adding');
              if (window.showToast) window.showToast('Could not add — please try again', 'error');
            });
        });
      });
    }
    window.CartRecs = { fetchList: fetchList, filterCards: filterCards, wireAdds: wireAdds, flyToCart: flyToCart };
  })();

  // --- Cart drawer ---
  (function() {
    if (window.SMGLR_CART_DRAWER_DISABLED) return;
    var drawer = null, backdrop = null, releaseTrap = null;
    function build() {
      if (drawer) return;
      backdrop = document.createElement('div');
      backdrop.className = 'cart-drawer__backdrop';
      drawer = document.createElement('aside');
      drawer.className = 'cart-drawer';
      drawer.setAttribute('role', 'dialog');
      drawer.setAttribute('aria-label', 'Shopping cart');
      drawer.setAttribute('data-lenis-prevent', '');
      drawer.innerHTML =
        '<aside class="cart-drawer__recs" data-cart-drawer-recs aria-label="Recommended products" hidden>' +
          '<p class="cart-drawer__recs-head">You may also like</p>' +
          '<div class="cart-drawer__recs-scroll" data-cart-drawer-recs-scroll></div>' +
        '</aside>' +
        '<div class="cart-drawer__panel">' +
          '<div class="cart-drawer__header">' +
            '<h2 class="cart-drawer__title">Your cart</h2>' +
            '<button type="button" class="cart-drawer__close" aria-label="Close cart"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>' +
          '</div>' +
          '<div class="cart-drawer__body" data-cart-drawer-body></div>' +
          '<div class="cart-drawer__footer" data-cart-drawer-footer hidden></div>' +
        '</div>';
      document.body.appendChild(backdrop);
      document.body.appendChild(drawer);
      drawer.querySelector('.cart-drawer__close').addEventListener('click', close);
      backdrop.addEventListener('click', close);
      document.addEventListener('keydown', function(e) { if (e.key === 'Escape' && drawer.classList.contains('is-open')) close(); });
    }
    function formatMoney(cents) { return '₹ ' + (cents/100).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2}); }
    var compareCache = {}; // handle -> { variantId: compare_at_price }
    function loadCompareData(items) {
      var unique = {};
      items.forEach(function(it) { if (it.handle && !compareCache[it.handle]) unique[it.handle] = true; });
      var handles = Object.keys(unique);
      if (!handles.length) return Promise.resolve();
      return Promise.all(handles.map(function(h) {
        return fetch('/products/' + h + '.js')
          .then(function(r) { return r.json(); })
          .then(function(p) {
            var map = {};
            p.variants.forEach(function(v) { map[v.id] = v.compare_at_price; });
            compareCache[h] = map;
          })
          .catch(function() {});
      }));
    }

    // Render visible line-item properties (combo selections, gift notes, …). Skips _hidden keys + blanks.
    function itemPropsHtml(item) {
      var props = item.properties;
      if (!props) return '';
      var rows = '';
      Object.keys(props).forEach(function(k) {
        if (!k || k.charAt(0) === '_') return;
        var v = props[k];
        if (v == null || v === '') return;
        rows += '<div class="cart-line-prop"><span class="cart-line-prop__k">' + escHtml(k) + ':</span> ' + escHtml(v) + '</div>';
      });
      return rows ? '<div class="cart-line-props">' + rows + '</div>' : '';
    }
    function buildHtml(cart) {
      var html = '<div class="cart-drawer__items">';
      cart.items.forEach(function(item) {
        var final = item.final_line_price != null ? item.final_line_price : item.line_price;
        var original = item.original_line_price != null ? item.original_line_price : final;
        var compareUnit = (compareCache[item.handle] && compareCache[item.handle][item.variant_id]) || 0;
        var strike = Math.max(compareUnit * item.quantity, original);   // highest "was" price: sale list AND/OR pre-discount
        var allocs = item.line_level_discount_allocations;
        var hasLineDiscount = allocs && allocs.length;
        var compareLine = '', saveLine = '';
        if (strike > final) {
          compareLine = ' <s class="cart-drawer__item-compare">' + formatMoney(strike) + '</s>';
          // The named discount pill already states the saving — skip the generic SAVE line when a discount applies.
          if (!hasLineDiscount) saveLine = '<div class="cart-drawer__item-save">SAVE ' + formatMoney(strike - final) + '</div>';
        }
        var discountLine = discountPills(allocs);
        var propsLine = itemPropsHtml(item);
        html += '<div class="cart-drawer__item" data-cart-drawer-line="'+item.key+'">' +
          '<img src="'+(item.image || '')+'" alt="'+item.title+'">' +
          '<div>' +
            '<a href="'+item.url+'" class="cart-drawer__item-title">'+item.product_title+'</a>' +
            '<div class="cart-drawer__item-price">'+formatMoney(final)+ compareLine +'</div>' +
            saveLine +
            discountLine +
            propsLine +
            '<div class="cart-drawer__item-actions">' +
              '<div class="qty-stepper cart-drawer__item-qty" data-drawer-qty data-line-key="'+item.key+'">' +
                '<button type="button" class="qty-stepper__btn" data-qty-decrease aria-label="Decrease quantity">&minus;</button>' +
                '<input type="number" value="'+item.quantity+'" min="0" class="qty-stepper__input" aria-label="Quantity" data-drawer-line-qty>' +
                '<button type="button" class="qty-stepper__btn" data-qty-increase aria-label="Increase quantity">+</button>' +
              '</div>' +
              '<button type="button" class="cart-drawer__item-remove" data-drawer-remove="'+item.key+'">Remove</button>' +
            '</div>' +
          '</div>' +
        '</div>';
      });
      return html;
    }

    // Line-quantity sync: the input updates optimistically on every +/-/keystroke, while the actual
    // /cart/update.js call is debounced + coalesced so rapid taps make ONE request. Only ONE request is ever
    // in flight — update.js does ABSOLUTE sets, so two overlapping ones could land out of order and corrupt qty.
    // pendingUpdates holds the desired qty per line key until the server confirms it (the authoritative value
    // that +/- build on and that re-renders restore, so a mid-interaction repaint can't reset the baseline).
    // Removal routes through the SAME queue (pendingUpdates[key]=0) so it can't race a pending qty flush and
    // resurrect the line. renderSeq guards the async compare re-paint from clobbering with a stale snapshot.
    var pendingUpdates = {}, syncTimer = null, syncing = false, syncFails = 0, renderSeq = 0;
    function scheduleSync(delay) { if (syncTimer) clearTimeout(syncTimer); syncTimer = setTimeout(flushSync, delay || 350); }
    function flushSync() {
      syncTimer = null;
      if (syncing) return;                       // one already in flight; its handler re-flushes anything still pending
      var sent = {}, keys = Object.keys(pendingUpdates);
      if (!keys.length) return;
      keys.forEach(function(k){ sent[k] = pendingUpdates[k]; });
      syncing = true;
      window.fetchWithTimeout('/cart/update.js', { method:'POST', headers:{'Content-Type':'application/json','Accept':'application/json'}, body: JSON.stringify({ updates: sent }) }, 8000)
        .then(function(r){ if (!r.ok) throw new Error('update'); return r.json(); })
        .then(function(cart){
          syncing = false; syncFails = 0;
          // Clear only the keys we synced that the user hasn't re-touched since (don't drop mid-flight taps).
          Object.keys(sent).forEach(function(k){ if (pendingUpdates[k] === sent[k]) delete pendingUpdates[k]; });
          render(cart); refreshCartState(); filterRecs(cart);
          if (Object.keys(pendingUpdates).length) scheduleSync();
        })
        .catch(function(){
          syncing = false;
          // Bounded backoff on failure/timeout — retry a few times, then stop hammering until the next edit.
          if (Object.keys(pendingUpdates).length && ++syncFails <= 3) scheduleSync(350 * syncFails);
        });
    }
    function removeLine(key, itemEl) {
      pendingUpdates[key] = 0;                    // absolute set to 0 == remove, via the SAME serialized queue
      syncFails = 0;
      if (itemEl) itemEl.remove();               // optimistic: the line disappears immediately
      if (window.showToast) window.showToast('Item removed', 'info');
      scheduleSync();
    }
    function wireItems(body) {
      body.querySelectorAll('[data-drawer-remove]').forEach(function(btn) {
        btn.addEventListener('click', function() { removeLine(btn.getAttribute('data-drawer-remove'), btn.closest('[data-cart-drawer-line]')); });
      });
      body.querySelectorAll('[data-drawer-qty]').forEach(function(stepper) {
        var input = stepper.querySelector('[data-drawer-line-qty]');
        var key = stepper.getAttribute('data-line-key');
        // A re-render (flush response / background compare paint) rebuilds inputs from the SERVER qty. Restore any
        // still-unsynced pending value so both the display and the +/- baseline stay correct mid-interaction.
        if (key in pendingUpdates) input.value = pendingUpdates[key];
        function base() { return (key in pendingUpdates) ? pendingUpdates[key] : (parseInt(input.value, 10) || 0); }
        function set(next) { next = Math.max(0, next); input.value = next; pendingUpdates[key] = next; syncFails = 0; scheduleSync(); }
        stepper.querySelector('[data-qty-increase]').addEventListener('click', function(){ set(base() + 1); });
        stepper.querySelector('[data-qty-decrease]').addEventListener('click', function(){ set(base() - 1); });
        input.addEventListener('change', function(){ var v = parseInt(input.value, 10); set(isNaN(v) ? 0 : v); });
      });
    }

    var NOTE_PLUS_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>';
    var NOTE_PENCIL_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>';
    function escHtml(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }
    // Summary mirroring the cart page: subtotal + compare-at strikethrough + savings + shipping note.
    // (Savings need compareCache, which loads async — render() refreshes this block once it arrives.)
    var TAG_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>';
    // Automatic-discount pills that NAME the offer on a line (e.g. "Welcome Offer −₹85").
    function discountPills(allocs) {
      allocs = allocs || [];
      if (!allocs.length) return '';
      var out = '<div class="cart-discounts">';
      allocs.forEach(function(a) {
        var title = (a.discount_application && a.discount_application.title) || 'Discount';
        out += '<span class="cart-discount-pill">' + TAG_SVG + '<span>' + escHtml(title) + '</span><span class="cart-discount-pill__amt">−' + formatMoney(a.amount) + '</span></span>';
      });
      return out + '</div>';
    }
    // Merge cart-level + line-level automatic discounts into a [{title, amount}] list, keyed by title.
    function aggregateDiscounts(cart, items) {
      var map = {}, order = [];
      function add(title, amt) { title = title || 'Discount'; if (!(title in map)) order.push(title); map[title] = (map[title] || 0) + (amt || 0); }
      (cart.cart_level_discount_applications || []).forEach(function(d) { add(d.title || (d.discount_application && d.discount_application.title), d.total_allocated_amount); });
      (items || cart.items || []).forEach(function(item) {
        (item.line_level_discount_allocations || []).forEach(function(a) { add(a.discount_application && a.discount_application.title, a.amount); });
      });
      return order.map(function(t) { return { title: t, amount: map[t] }; });
    }
    // Summary mirroring the cart page. When an automatic discount is active it shows the breakdown
    // (Subtotal before discount -> named discount rows -> Total), so the shopper SEES the offer + amount.
    // Otherwise it falls back to the compare-at "you save" line (needs compareCache, loaded async).
    function summaryInnerHtml(cart, items) {
      items = items || cart.items || [];
      var discounts = aggregateDiscounts(cart, items);
      var totalDiscount = 0; discounts.forEach(function(d) { totalDiscount += d.amount; });
      var html = '';
      if (totalDiscount > 0) {
        var originalTotal = cart.original_total_price != null ? cart.original_total_price : (cart.total_price + totalDiscount);
        html += '<div class="cart-drawer__summary-row"><span>Subtotal</span><span>' + formatMoney(originalTotal) + '</span></div>';
        discounts.forEach(function(d) {
          html += '<div class="cart-drawer__summary-row cart-drawer__summary-row--discount"><span>' + TAG_SVG + '<span>' + escHtml(d.title) + '</span></span><span>−' + formatMoney(d.amount) + '</span></div>';
        });
        html += '<div class="cart-drawer__subtotal"><span>Total</span><span class="cart-drawer__subtotal-prices"><span class="cart-drawer__subtotal-current">' + formatMoney(cart.total_price) + '</span></span></div>';
      } else {
        var compareTotal = 0, hasCompare = false;
        items.forEach(function(item) {
          var cu = (compareCache[item.handle] && compareCache[item.handle][item.variant_id]) || 0;
          var fp = item.final_line_price != null ? item.final_line_price : item.line_price;
          if (cu && cu * item.quantity > fp) { compareTotal += cu * item.quantity; hasCompare = true; } else { compareTotal += fp; }
        });
        html += '<div class="cart-drawer__subtotal"><span>Subtotal</span><span class="cart-drawer__subtotal-prices">' +
          '<span class="cart-drawer__subtotal-current">' + formatMoney(cart.total_price) + '</span>';
        if (hasCompare && compareTotal > cart.total_price) {
          html += '<s class="cart-drawer__subtotal-compare">' + formatMoney(compareTotal) + '</s>' +
            '<span class="cart-drawer__subtotal-save">Save ' + formatMoney(compareTotal - cart.total_price) + '</span>';
        }
        html += '</span></div>';
      }
      html += '<p class="cart-drawer__shipping-note">Shipping &amp; taxes calculated at checkout</p>';
      return html;
    }
    // Collapsible order note — same feature + persistence (cart.note) as the cart page, adapted to a toggle:
    // "Add order special instructions" -> box + Save -> collapses to "Edit order special instructions".
    // Because it writes cart.note, the cart page's textarea pre-fills with it on navigation (and vice-versa).
    function noteHtml(cart) {
      var note = cart.note || '';
      var hasNote = note.replace(/\s/g, '').length > 0;
      return '<div class="cart-drawer__note" data-drawer-note>' +
          '<button type="button" class="cart-drawer__note-toggle" data-drawer-note-toggle aria-expanded="false">' +
            '<span class="cart-drawer__note-ico" data-drawer-note-icon>' + (hasNote ? NOTE_PENCIL_SVG : NOTE_PLUS_SVG) + '</span>' +
            '<span data-drawer-note-label>' + (hasNote ? 'Edit' : 'Add') + ' order special instructions</span>' +
          '</button>' +
          '<div class="cart-drawer__note-editor" data-drawer-note-editor hidden>' +
            '<textarea class="cart-drawer__note-input" data-drawer-note-input maxlength="250" rows="4" placeholder="Add a note for your order...">' + escHtml(note) + '</textarea>' +
            '<div class="cart-drawer__note-foot">' +
              '<span class="cart-drawer__note-counter"><span data-drawer-note-count>' + note.length + '</span> / 250</span>' +
              '<div class="cart-drawer__note-actions">' +
                '<button type="button" class="cart-drawer__note-cancel" data-drawer-note-cancel>Cancel</button>' +
                '<button type="button" class="cart-drawer__note-save" data-drawer-note-save>Save</button>' +
              '</div>' +
            '</div>' +
          '</div>' +
        '</div>';
    }
    function wireNote(cart) {
      var f = drawer.querySelector('[data-cart-drawer-footer]');
      var wrap = f && f.querySelector('[data-drawer-note]');
      if (!wrap) return;
      var toggle = wrap.querySelector('[data-drawer-note-toggle]');
      var editor = wrap.querySelector('[data-drawer-note-editor]');
      var input = wrap.querySelector('[data-drawer-note-input]');
      var count = wrap.querySelector('[data-drawer-note-count]');
      var iconEl = wrap.querySelector('[data-drawer-note-icon]');
      var labelEl = wrap.querySelector('[data-drawer-note-label]');
      var saveBtn = wrap.querySelector('[data-drawer-note-save]');
      var cancelBtn = wrap.querySelector('[data-drawer-note-cancel]');
      var savedNote = cart.note || '';
      function setOpen(open) {
        if (open) { editor.removeAttribute('hidden'); toggle.setAttribute('aria-expanded', 'true'); input.focus(); }
        else { editor.setAttribute('hidden', ''); toggle.setAttribute('aria-expanded', 'false'); }
      }
      toggle.addEventListener('click', function() { setOpen(editor.hasAttribute('hidden')); });
      input.addEventListener('input', function() { count.textContent = input.value.length; });
      cancelBtn.addEventListener('click', function() { input.value = savedNote; count.textContent = savedNote.length; setOpen(false); });
      saveBtn.addEventListener('click', function() {
        var val = input.value;
        saveBtn.disabled = true;
        window.fetchWithTimeout('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ note: val }) }, 8000)
          .then(function(r) { if (!r.ok) throw new Error('note'); return r.json(); })
          .then(function() {
            savedNote = val;
            var has = val.replace(/\s/g, '').length > 0;
            if (labelEl) labelEl.textContent = (has ? 'Edit' : 'Add') + ' order special instructions';
            if (iconEl) iconEl.innerHTML = has ? NOTE_PENCIL_SVG : NOTE_PLUS_SVG;
            setOpen(false);
            saveBtn.disabled = false;
            // If we're on the cart page, keep its always-visible note field in sync too.
            var pageNote = document.querySelector('[data-cart-note]');
            if (pageNote && pageNote.value !== val) { pageNote.value = val; var pc = document.querySelector('[data-note-count]'); if (pc) pc.textContent = val.length; }
            if (window.showToast) window.showToast('Instructions saved', 'success');
          })
          .catch(function() { saveBtn.disabled = false; if (window.showToast) window.showToast('Could not save instructions', 'error'); });
      });
    }
    function render(cart) {
      var body = drawer.querySelector('[data-cart-drawer-body]');
      var footer = drawer.querySelector('[data-cart-drawer-footer]');
      // Bump on EVERY render — including the empty-state path below — so any stale in-flight compare re-paint from an
      // earlier render is dropped by its token guard. (Otherwise removing the last line takes the empty path, which
      // would skip the bump, and a late compare callback whose pending key was already flush-deleted resurrects it.)
      var token = ++renderSeq;
      // Hide lines the user just removed (pending qty 0) so an out-of-order repaint can't resurrect them.
      var items = ((cart && cart.items) || []).filter(function(i){ return pendingUpdates[i.key] !== 0; });
      if (!items.length) {
        body.innerHTML = '<div class="cart-drawer__empty"><p>Your cart is empty.</p><a href="/collections/all" class="btn btn--primary">Shop the drop</a></div>';
        footer.hidden = true;
        return;
      }
      var view = { items: items, total_price: cart.total_price };
      // First paint without compare data (fast)
      body.innerHTML = buildHtml(view) + '</div>';
      footer.hidden = false;
      footer.innerHTML =
        /* Order note is intentionally NOT rendered in the drawer on any device — shoppers add
           instructions on the cart page instead. Re-enable by restoring `noteHtml(cart) +` here
           and the `wireNote(cart);` call below (both functions are kept intact). */
        '<div class="cart-drawer__summary" data-drawer-summary>' + summaryInnerHtml(cart, items) + '</div>' +
        /* "View cart" is intentionally NOT rendered — the drawer goes straight to checkout.
           Re-enable by restoring this line:
           '<a href="/cart" class="btn btn--secondary btn--full" style="margin-bottom:8px">View cart</a>' + */
        '<a href="/checkout" class="btn btn--primary btn--full">Checkout</a>';
      wireItems(body);
      // Fetch compare prices in the background, then re-render items + summary WITH savings — unless a newer render
      // superseded us OR a line was removed meanwhile: re-derive from live pendingUpdates so a just-removed line
      // isn't painted back. Only the items + the [data-drawer-summary] block are repainted, so the note editor
      // (a sibling in the footer) keeps its open/typing state.
      loadCompareData(items).then(function() {
        if (token !== renderSeq) return;
        var live = ((cart && cart.items) || []).filter(function(i){ return pendingUpdates[i.key] !== 0; });
        if (!live.length) return;
        body.innerHTML = buildHtml({ items: live, total_price: cart.total_price }) + '</div>';
        wireItems(body);
        var sum = footer.querySelector('[data-drawer-summary]');
        if (sum) sum.innerHTML = summaryInnerHtml(cart, live);
      });
    }
    // --- Recommendation bar (left of the panel; horizontal strip on mobile) ---
    var recLoaded = false, drawerSettled = false, recsPendingReveal = false;
    function recEls() { return { bar: drawer && drawer.querySelector('[data-cart-drawer-recs]'), scroll: drawer && drawer.querySelector('[data-cart-drawer-recs-scroll]') }; }
    function revealRecsBar() {
      var e = recEls();
      if (!e.bar) return;
      var wasHidden = e.bar.hidden;
      e.bar.hidden = false;
      if (wasHidden) { e.bar.classList.remove('is-revealed'); void e.bar.offsetWidth; e.bar.classList.add('is-revealed'); }  // animate only the first reveal
    }
    // Reveal the rec bar only once the drawer has finished sliding in, so growing the drawer can't re-resolve
    // the %-based slide transform mid-animation (desktop stutter) or shove the cart down mid-slide (mobile).
    function revealRecsWhenSettled() { if (drawerSettled) revealRecsBar(); else recsPendingReveal = true; }
    function filterRecs(cart) {
      var e = recEls();
      if (!e.scroll || !e.bar || !window.CartRecs) return;
      if (window.CartRecs.filterCards(e.scroll, cart) === 0) e.bar.hidden = true;
      else revealRecsWhenSettled();
    }
    function loadRecs(cart) {
      var e = recEls();
      if (!e.scroll || !e.bar || !window.CartRecs) return;
      if (recLoaded) { filterRecs(cart); return; }
      var seed = (cart.items && cart.items[0] && cart.items[0].product_id) || window.cartRecSeed;
      if (!seed) { e.bar.hidden = true; return; }
      window.CartRecs.fetchList(seed, 10).then(function(html) {
        // Guard: the drawer may have been closed (or closed+reopened) while the fetch was in flight — don't
        // grow/reveal a closed drawer.
        if (!html || !drawer || !drawer.classList.contains('is-open')) { if (e.bar) e.bar.hidden = true; return; }
        e.scroll.innerHTML = html;
        recLoaded = true;
        window.CartRecs.wireAdds(e.scroll, { onAdd: function(freshCart) { render(freshCart); filterRecs(freshCart); } });
        filterRecs(cart);
      });
    }
    // Real scroll-lock: lenis.stop() alone leaves the page scrollable for reduced-motion (no lenis) and all
    // touch users, so also lock the body. Pad for the removed scrollbar so the page behind doesn't jump.
    function lockScroll() {
      var sw = window.innerWidth - document.documentElement.clientWidth;
      if (sw > 0) document.body.style.paddingRight = sw + 'px';
      document.body.classList.add('cart-drawer-open');
    }
    function unlockScroll() { document.body.classList.remove('cart-drawer-open'); document.body.style.paddingRight = ''; }
    // Fire cb once the open transition (transform) settles, with a timer fallback.
    function onDrawerSettled(cb) {
      var done = false;
      function fin() { if (done) return; done = true; drawer.removeEventListener('transitionend', te); cb(); }
      function te(ev) { if (ev.target === drawer && ev.propertyName === 'transform') fin(); }
      drawer.addEventListener('transitionend', te);
      setTimeout(fin, 620);
    }
    function open() {
      build();
      // Open immediately with skeleton, then load cart data
      var body = drawer.querySelector('[data-cart-drawer-body]');
      if (body) body.innerHTML = window.skeletonLines ? window.skeletonLines(3) : '';
      backdrop.classList.add('is-open');
      drawer.classList.add('is-open');
      lockScroll();
      if (window.lenis) window.lenis.stop();
      releaseTrap = window.trapFocus(drawer);
      // Defer the rec-bar reveal until the drawer has finished sliding in (see revealRecsWhenSettled).
      drawerSettled = false; recsPendingReveal = false;
      onDrawerSettled(function() { drawerSettled = true; if (recsPendingReveal) { recsPendingReveal = false; revealRecsBar(); } });
      fetch('/cart.js').then(function(r){ return r.json(); }).then(function(cart) {
        render(cart);
        loadRecs(cart);
      });
    }
    function close() {
      if (!drawer) return;
      drawer.classList.remove('is-open');
      backdrop.classList.remove('is-open');
      unlockScroll();
      if (window.lenis) window.lenis.start();
      if (releaseTrap) { releaseTrap(); releaseTrap = null; }
      // Clear the box-shadow artifact after transition completes
      setTimeout(function() {
        if (drawer && !drawer.classList.contains('is-open')) {
          drawer.style.visibility = 'hidden';
        }
      }, 550);
    }
    // Restore visibility before opening
    var origOpen = open;
    open = function() {
      if (drawer) drawer.style.visibility = '';
      origOpen();
    };
    window.openCartDrawer = open;
    window.closeCartDrawer = close;
    // Auto-open after an add-to-cart. Every add path calls THIS rather than open(), because
    // the drawer is a merchant setting (settings.cart_drawer_enabled) that is not exposed to
    // JS -- the theme signals it by rendering data-cart-drawer-toggle onto the cart links
    // (see header.liquid / mobile-cart-bar.liquid). Its presence therefore IS the setting, so
    // a merchant who turns the drawer off keeps plain cart navigation instead of a drawer
    // appearing on every add.
    window.revealCartAfterAdd = function() {
      if (!document.querySelector('[data-cart-drawer-toggle]')) return;
      open();
    };
    // Header cart icon opens drawer (theme setting controls this)
    document.addEventListener('click', function(e) {
      var cartLink = e.target.closest('[data-cart-drawer-toggle]');
      if (!cartLink) return;
      e.preventDefault();
      open();
    });
  })();

  // --- Predictive search ---
  (function() {
    var input = null, results = null, wrap = null, timer = null;
    function build() {
      if (wrap) return;
      var host = document.querySelector('[data-predictive-search-host]');
      if (!host) return;
      wrap = document.createElement('div');
      wrap.className = 'predictive-search';
      wrap.innerHTML = '<input type="search" class="predictive-search__input" placeholder="Search products..." aria-label="Search"><div class="predictive-search__results" data-predictive-results></div>';
      host.style.position = 'relative';
      host.appendChild(wrap);
      input = wrap.querySelector('input');
      results = wrap.querySelector('[data-predictive-results]');
      input.addEventListener('input', onInput);
      document.addEventListener('click', function(e) { if (!wrap.contains(e.target) && !e.target.closest('[data-predictive-toggle]')) close(); });
    }
    function onInput() {
      var q = input.value.trim();
      clearTimeout(timer);
      if (!q) { results.innerHTML = ''; return; }
      timer = setTimeout(function() {
        results.innerHTML = window.skeletonLines ? window.skeletonLines(3) : '<p class="predictive-search__empty">Searching...</p>';
        window.fetchWithTimeout('/search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=6', {}, 5000)
          .then(function(r){ return r.json(); })
          .then(function(data) {
            var products = (data.resources && data.resources.results && data.resources.results.products) || [];
            if (!products.length) { results.innerHTML = '<p class="predictive-search__empty">No results for "'+q+'"</p>'; return; }
            var html = '';
            products.forEach(function(p) {
              html += '<a class="predictive-search__result" href="'+p.url+'">' +
                (p.image ? '<img src="'+p.image+'" alt="">' : '') +
                '<div><p class="predictive-search__result-title">'+p.title+'</p><p class="predictive-search__result-price">'+(p.price||'')+'</p></div>' +
              '</a>';
            });
            results.innerHTML = html;
          })
          .catch(function() { window.renderAsyncError(results, 'Search failed.'); });
      }, 250);
    }
    function open() { build(); if (wrap) { wrap.classList.add('is-open'); setTimeout(function(){ input && input.focus(); }, 80); } }
    function close() { if (wrap) wrap.classList.remove('is-open'); }
    window.openPredictiveSearch = open;
    window.closePredictiveSearch = close;
    document.addEventListener('click', function(e) {
      var toggle = e.target.closest('[data-predictive-toggle]');
      if (!toggle) return;
      e.preventDefault();
      wrap && wrap.classList.contains('is-open') ? close() : open();
    });
  })();

  // --- Recently viewed products ---
  (function() {
    var KEY = 'smglr-recently-viewed';
    function read() { try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch(e) { return []; } }
    function write(arr) { try { localStorage.setItem(KEY, JSON.stringify(arr.slice(0, 8))); } catch(e) {} }
    window.trackRecentlyViewed = function(handle) {
      if (!handle) return;
      var list = read().filter(function(h) { return h !== handle; });
      list.unshift(handle);
      write(list);
    };
    // Auto-track on product page
    var productSection = document.querySelector('[data-product-section]');
    if (productSection) {
      try {
        var data = JSON.parse(productSection.getAttribute('data-product-json'));
        if (data && data.handle) window.trackRecentlyViewed(data.handle);
      } catch(e) {}
    }
    // Auto-render into [data-recently-viewed-grid]
    var grid = document.querySelector('[data-recently-viewed-grid]');
    if (grid) {
      var handles = read();
      var currentHandle = grid.getAttribute('data-exclude') || '';
      handles = handles.filter(function(h) { return h !== currentHandle; });
      if (!handles.length) {
        var wrapper = grid.closest('.recently-viewed');
        if (wrapper) wrapper.style.display = 'none';
      } else {
        var swiperWrapper = grid.querySelector('.swiper-wrapper') || grid;
        var toLoad = handles.slice(0, 4);
        swiperWrapper.innerHTML = window.skeletonProductGrid ? window.skeletonProductGrid(toLoad.length) : '';
        var loaded = [];
        var pending = toLoad.length;
        toLoad.forEach(function(h) {
          fetch('/products/' + h + '?view=card')
            // Skip non-200s: a stale/invalid handle returns the 404 page wrapped in the full
            // theme layout (splash screen, newsletter cup, footer). Injecting that raw is what
            // leaked page chrome into the grid.
            .then(function(r){ return r.ok ? r.text() : ''; })
            .then(function(html) {
              if (!html) return;
              // Belt-and-suspenders: only ever take the product card itself, never any
              // surrounding layout, even if a response somehow comes back wrapped.
              var t = document.createElement('div');
              t.innerHTML = html;
              var card = t.querySelector('[data-product-card]');
              if (card) loaded.push('<div class="swiper-slide">' + card.outerHTML + '</div>');
            })
            .catch(function(){})
            .finally(function() {
              pending--;
              if (pending <= 0) {
                swiperWrapper.innerHTML = loaded.join('') || '';
                if (window.initSwipers) window.initSwipers();
                if (window.initScrollReveals) window.initScrollReveals();
                if (!loaded.length) {
                  var emptyWrap = grid.closest('.recently-viewed');
                  if (emptyWrap) emptyWrap.style.display = 'none';
                }
              }
            });
        });
      }
    }
  })();

  // --- Combo maker (shared by the PDP + the quick-add modal) ---
  // One dropdown per item in the pack. `size` (combo_size) is the number of dropdowns and is
  // INDEPENDENT of the option list, so 3 options with a size of 20 renders 20 dropdowns each
  // offering those 3. Every pick counts once and there is no upper limit; when `size` is missing
  // it falls back to the option count. The picks become line-item PROPERTIES on add-to-cart, so
  // they ride the cart -> checkout -> the admin order (no app/backend). Config JSON:
  //   { "size":20, "label":"Origin", "heading":"Build your 20-pack", "options":["Ethiopia", ...] }
  window.ComboMaker = (function() {
    var uid = 0;
    // A line-item property VALUE is length-capped by Shopify; a large combo is therefore split
    // across "Combo (1)", "Combo (2)", ... so nothing is silently truncated in the order.
    var PROP_CHUNK = 220;
    function esc(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }
    function build(container, cfg) {
      if (!container) return;
      if (!cfg || !Array.isArray(cfg.options) || !cfg.options.length) { container.hidden = true; return; }
      var opts = cfg.options.filter(function(o) { return o != null && String(o).length; });
      if (!opts.length) { container.hidden = true; return; }
      var size = parseInt(cfg.size, 10);            // dropdowns = combo_size (independent of options)
      if (!(size >= 1)) size = opts.length;         // not set -> one per option
      var label = (cfg.label && String(cfg.label).trim()) || 'Selection';
      var heading = (cfg.heading && String(cfg.heading).trim()) || ('Build your ' + size + '-pack');
      var id = ++uid;
      var optsHtml = '<option value="" disabled selected>Select ' + esc(label) + '…</option>';
      opts.forEach(function(o) { optsHtml += '<option value="' + esc(o) + '">' + esc(o) + '</option>'; });
      var slots = '';
      for (var i = 0; i < size; i++) {
        var sid = 'combo-' + id + '-' + i;
        slots += '<div class="combo-maker__slot">' +
            '<label class="combo-maker__slot-label" for="' + sid + '">' + esc(label) + ' ' + (i + 1) + '</label>' +
            '<div class="combo-maker__select-wrap">' +
              '<select class="combo-maker__select js-custom-select" id="' + sid + '" data-combo-select aria-required="true">' + optsHtml + '</select>' +
            '</div>' +
          '</div>';
      }
      container.innerHTML =
        '<div class="combo-maker__head">' +
          '<h3 class="combo-maker__title">' + esc(heading) + '</h3>' +
          '<p class="combo-maker__hint">Choose one ' + esc(label).toLowerCase() + ' for each of the <strong>' + size + '</strong> items in your combo.</p>' +
        '</div>' +
        '<div class="combo-maker__slots">' + slots + '</div>' +
        '<div class="combo-maker__summary" data-combo-summary role="status" aria-live="polite"></div>';
      container.hidden = false;
      container.__combo = { size: size, label: label };
      if (!container.__comboWired) {
        container.__comboWired = true;
        container.addEventListener('change', function(e) {
          if (e.target && e.target.matches && e.target.matches('[data-combo-select]')) { container.classList.remove('is-invalid'); e.target.classList.remove('is-error'); updateSummary(container); }
        });
      }
      updateSummary(container);
      if (window.initCustomSelects) window.initCustomSelects();   // enhance to the theme's custom dropdown
    }
    function selections(container) {
      var out = [];
      container.querySelectorAll('[data-combo-select]').forEach(function(s) { out.push({ value: s.value, el: s }); });
      return out;
    }
    // Group identical picks so the summary/order read "Ethiopia x 2" rather than listing it twice.
    function grouped(values) {
      var counts = {}, order = [];
      values.forEach(function(v) { if (!(v in counts)) { counts[v] = 0; order.push(v); } counts[v]++; });
      return order.map(function(v) { return { value: v, count: counts[v] }; });
    }
    function partsOf(values) {
      return grouped(values).map(function(g) { return g.count > 1 ? g.value + ' × ' + g.count : g.value; });
    }
    function updateSummary(container) {
      var cfg = container.__combo; if (!cfg) return;
      var sel = selections(container);
      var chosen = sel.filter(function(s) { return s.value; });
      var complete = chosen.length === sel.length;
      container.classList.toggle('is-complete', complete);
      var summary = container.querySelector('[data-combo-summary]');
      if (!summary) return;
      if (!complete) {
        summary.className = 'combo-maker__summary';
        summary.innerHTML = '<strong>' + chosen.length + ' of ' + sel.length + '</strong> chosen — pick ' + (sel.length - chosen.length) + ' more to complete your combo.';
      } else {
        var parts = partsOf(chosen.map(function(s) { return s.value; })).map(esc);
        summary.className = 'combo-maker__summary is-complete';
        summary.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg> Your combo: <strong>' + parts.join(', ') + '</strong> — ' + cfg.size + ' items.';
      }
    }
    // Validate + return { ok, properties }. Properties are chunked so a large combo can't be truncated.
    function collect(container) {
      if (!container) return { ok: true, properties: null };
      var cfg = container.__combo;
      if (!cfg) {  // a combo WAS intended but never built — fail CLOSED, never add bare
        container.classList.add('is-invalid');
        return { ok: false };
      }
      var sel = selections(container), ok = true, firstBad = null, values = [];
      sel.forEach(function(s) {
        if (!s.value) { ok = false; if (!firstBad) firstBad = s.el; s.el.classList.add('is-error'); return; }
        s.el.classList.remove('is-error');
        values.push(s.value);
      });
      if (!ok) {
        container.classList.add('is-invalid');
        var summary = container.querySelector('[data-combo-summary]');
        if (summary) { summary.className = 'combo-maker__summary is-error'; summary.innerHTML = 'Please choose all ' + sel.length + ' selections to build your combo.'; }
        if (firstBad) { try { firstBad.focus(); } catch (e) {} if (container.scrollIntoView) try { container.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {} }
        return { ok: false };
      }
      // Join the picks, splitting across numbered properties if the value would get too long.
      var parts = partsOf(values), chunks = [], cur = '';
      parts.forEach(function(t) {
        var next = cur ? cur + ', ' + t : t;
        if (next.length > PROP_CHUNK && cur) { chunks.push(cur); cur = t; } else { cur = next; }
      });
      if (cur) chunks.push(cur);
      var props = {};
      if (chunks.length === 1) props['Combo'] = chunks[0];
      else chunks.forEach(function(c, i) { props['Combo (' + (i + 1) + ')'] = c; });
      return { ok: true, properties: props };
    }
    function init(container) {
      if (!container || container.hasAttribute('data-combo-inited')) return;
      container.setAttribute('data-combo-inited', '');
      var cfg = null;
      try { cfg = JSON.parse(container.getAttribute('data-combo') || 'null'); } catch (e) {}
      if (cfg) { try { build(container, cfg); } catch (e) { container.hidden = true; } } else container.hidden = true;
    }
    function initAll(root) { (root || document).querySelectorAll('[data-combo-maker]:not([data-combo-inited])').forEach(init); }
    return { build: build, init: init, initAll: initAll, collect: collect, selections: selections };
  })();
  ComboMaker.initAll(document);   // PDP containers are server-rendered; the modal builds its own

  // --- Quick add modal ---
  (function() {
    var overlay = null;
    function build() {
      if (overlay) return;
      overlay = document.createElement('div');
      overlay.className = 'quick-add-overlay';
      overlay.innerHTML = '<div class="quick-add-modal" role="dialog" aria-modal="true" data-lenis-prevent><button type="button" class="quick-add-modal__close" aria-label="Close"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button><div data-quick-add-body></div></div>';
      document.body.appendChild(overlay);
      overlay.querySelector('.quick-add-modal__close').addEventListener('click', close);
      overlay.addEventListener('click', function(e) { if (e.target === overlay) close(); });
    }
    var releaseTrap = null;
    function open(handle, sourceBtn) {
      build();
      var body = overlay.querySelector('[data-quick-add-body]');
      body.innerHTML = window.skeletonLines ? window.skeletonLines(4) : 'Loading...';
      overlay.classList.add('is-open');
      if (window.lenis) window.lenis.stop();
      releaseTrap = window.trapFocus(overlay.querySelector('.quick-add-modal'));
      // Pull metafield-driven props from the trigger button (not in /products/.js)
      var ds = (sourceBtn && sourceBtn.dataset) || {};
      var preRatingValue = parseFloat(ds.rating) || 0;
      var preRatingCount = parseInt(ds.ratingCount) || 0;
      var preIntensity = parseInt(ds.intensity) || 0;
      var preIntensityLabel = ds.intensityLabel || '';
      var preIntensityFill = ds.intensityFill || '';
      var preIntensityAccent = ds.intensityAccent || '';
      var preAccentColor = ds.accentColor || '';
      var preVariantLabel = ds.variantLabel || '';
      var preVariantLabelStyle = ds.variantLabelStyle || '';
      var preShortDesc = ds.shortDescription || '';
      var preOrigin = ds.origin || '';
      var preWeightG = parseInt(ds.weightG) || 0;
      var preCups = parseInt(ds.cups) || 0;
      var preCombo = ds.combo || '';
      window.fetchWithTimeout('/products/' + handle + '.js', {}, 8000)
        .then(function(r){ return r.json(); })
        .then(function(p) {
          var variant = p.variants[0];
          var ratingValue = preRatingValue;
          var ratingCount = preRatingCount;
          var intensity = preIntensity;
          var origin = preOrigin;
          var weightG = preWeightG;
          var cupsPerPack = preCups;
          var custom = { variant_label: preVariantLabel, variant_label_style: preVariantLabelStyle };
          // Admin-selectable label style (custom.variant_label_style). Blank -> the modal's default 600 weight.
          var vlCss = custom.variant_label_style === 'bold' ? 'font-weight:700;font-style:normal'
            : custom.variant_label_style === 'italic' ? 'font-weight:400;font-style:italic'
            : custom.variant_label_style === 'bold-italic' ? 'font-weight:700;font-style:italic'
            : 'font-weight:600';
          var pricePerUnit = '';
          if (weightG > 0) pricePerUnit = '₹ ' + (variant.price * 100 / weightG / 100).toFixed(2) + ' / 100g';
          else if (cupsPerPack > 0) pricePerUnit = '₹ ' + (variant.price / cupsPerPack / 100).toFixed(2) + ' / cup';

          // Pick a lower stock variant if any to surface urgency
          var lowStockVariant = null;
          for (var i = 0; i < p.variants.length; i++) {
            if (p.variants[i].inventory_management && p.variants[i].inventory_quantity > 0 && p.variants[i].inventory_quantity <= 10) {
              lowStockVariant = p.variants[i]; break;
            }
          }
          var compareHtml = '';
          var saveHtml = '<div data-qv-save style="color:var(--color-green);font-family:var(--font-body);font-weight:600;font-size:13px;text-transform:uppercase;letter-spacing:0.4px;margin-top:4px"></div>';
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            compareHtml = ' <s style="color:var(--color-gray);font-weight:600;font-size:16px">₹ ' + (variant.compare_at_price/100).toFixed(2) + '</s>';
          }
          function buildSaveText(price, compare) {
            if (compare && compare > price) return 'Save ₹ ' + ((compare - price)/100).toFixed(2);
            return '';
          }
          // Stock status
          var stockText = '';
          if (!p.available) {
            stockText = '<div style="color:var(--color-primary);font-weight:700;font-size:12px;text-transform:uppercase;letter-spacing:0.4px;margin-top:6px">Sold out</div>';
          } else if (lowStockVariant) {
            stockText = ''; // already shown via low-stock urgency below
          } else {
            stockText = '<div style="color:var(--color-green);font-weight:700;font-size:12px;text-transform:uppercase;letter-spacing:0.4px;margin-top:6px">In stock</div>';
          }
          var soldOutOverlay = !p.available
            ? '<div class="product-card__sold-out-overlay" style="position:absolute"><span>Sold Out</span></div>'
            : '';

          // Card-level badges (New / Hot / Sale)
          var topBadge = '';
          var tagsLower = (p.tags || []).map(function(t){ return (t || '').toLowerCase(); });
          if (tagsLower.indexOf('new') > -1) topBadge = '<span class="product-card__badge">New</span>';
          else if (tagsLower.indexOf('hot') > -1) topBadge = '<span class="product-card__badge product-card__badge--hot">Hot</span>';
          else if (tagsLower.indexOf('sale') > -1) topBadge = '<span class="product-card__badge product-card__badge--sale">Sale</span>';

          // Wishlist active state
          var wishlistActive = '';
          try { var wl = JSON.parse(localStorage.getItem('smglr-wishlist') || '[]'); if (wl.indexOf(p.handle) > -1) wishlistActive = ' is-active'; } catch(e) {}

          // Stars row
          var starsHtml = '';
          if (ratingValue > 0 || ratingCount > 0) {
            starsHtml = '<div class="qv-stars" style="display:flex;align-items:center;gap:8px;margin:6px 0 12px"><div style="display:flex;gap:2px">';
            for (var s = 1; s <= 5; s++) {
              if (ratingValue >= s) starsHtml += '<svg width="14" height="14" viewBox="0 0 18 17" fill="var(--color-star)"><path d="M9 0l2.47 5.01L17.1 5.83l-4.05 3.95.96 5.57L9 12.77l-5.01 2.58.96-5.57L.9 5.83l5.63-.82L9 0z"/></svg>';
              else if (ratingValue > s - 1) starsHtml += '<svg width="14" height="14" viewBox="0 0 18 17"><defs><linearGradient id="qv-h-' + s + '"><stop offset="50%" stop-color="var(--color-star)"/><stop offset="50%" stop-color="transparent"/></linearGradient></defs><path d="M9 0l2.47 5.01L17.1 5.83l-4.05 3.95.96 5.57L9 12.77l-5.01 2.58.96-5.57L.9 5.83l5.63-.82L9 0z" fill="url(#qv-h-' + s + ')" stroke="var(--color-star)"/></svg>';
              else starsHtml += '<svg width="14" height="14" viewBox="0 0 18 17" fill="none" stroke="var(--color-star)"><path d="M9 0l2.47 5.01L17.1 5.83l-4.05 3.95.96 5.57L9 12.77l-5.01 2.58.96-5.57L.9 5.83l5.63-.82L9 0z"/></svg>';
            }
            starsHtml += '</div><span style="font-size:12px;color:var(--color-gray)">' + ratingCount + ' reviews</span></div>';
          }

          // Intensity dots — rendered inline, beside product type (colors + label from metafields via data attrs)
          var intensityHtml = '';
          if (intensity > 0) {
            var iFill = preIntensityFill || '#ec1e24';
            var iAccent = preIntensityAccent || '#ffe7c3';
            intensityHtml = '<span style="display:inline-flex;gap:6px;align-items:center;vertical-align:middle">';
            for (var x = 1; x <= 5; x++) {
              if (x <= intensity) intensityHtml += '<svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="8.62" fill="'+iFill+'"/><circle cx="12" cy="12" r="5.03" fill="none" stroke="'+iAccent+'" stroke-width="0.96"/><line x1="12" y1="3.33" x2="12" y2="20.67" stroke="'+iAccent+'" stroke-width="0.96"/><line x1="3.33" y1="12" x2="20.67" y2="12" stroke="'+iAccent+'" stroke-width="0.96"/><line x1="12" y1="0" x2="12" y2="3.33" stroke="'+iFill+'" stroke-width="0.96"/><line x1="12" y1="20.67" x2="12" y2="24" stroke="'+iFill+'" stroke-width="0.96"/><line x1="0" y1="12" x2="3.33" y2="12" stroke="'+iFill+'" stroke-width="0.96"/><line x1="20.67" y1="12" x2="24" y2="12" stroke="'+iFill+'" stroke-width="0.96"/></svg>';
              else intensityHtml += '<svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="8.03" fill="none" stroke="#000" stroke-width="0.94"/><circle cx="12" cy="12" r="5.03" fill="none" stroke="#000" stroke-width="0.94"/><line x1="12" y1="0" x2="12" y2="24" stroke="#000" stroke-width="0.94"/><line x1="0" y1="12" x2="24" y2="12" stroke="#000" stroke-width="0.94"/></svg>';
            }
            intensityHtml += '</span>';
            if (preIntensityLabel) intensityHtml += '<span style="color:#6d6e70;font-family:\'Outfit\',sans-serif;font-weight:400;font-size:14px;letter-spacing:0.3px">' + preIntensityLabel + '</span>';
          }

          // Origin / sustainability chip
          var originHtml = origin ? '<span class="product-card__badge product-card__badge--origin" style="position:static;display:inline-block;margin-bottom:10px">' + origin + '</span>' : '';

          var html = '' +
            '<div class="qv-action-row" style="display:flex;gap:8px;justify-content:flex-end;margin:0 44px 8px 0">' +
              '<button type="button" class="product-card__wishlist' + wishlistActive + '" style="position:static" data-product-handle="' + p.handle + '" aria-label="Add to wishlist"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 22"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" stroke-width="2"/></svg></button>' +
              '<button type="button" class="product-card__action-btn" data-compare-toggle data-handle="' + p.handle + '" data-title="' + (p.title || '').replace(/"/g, '&quot;') + '" data-image="' + (p.featured_image || '') + '" aria-label="Compare"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 3h5v5"/><path d="M4 20L21 3"/><path d="M21 16v5h-5"/><path d="M15 15l6 6"/><path d="M4 4l5 5"/></svg></button>' +
              '<button type="button" class="product-card__action-btn" data-share-product data-share-url="' + window.location.origin + p.url + '" data-share-title="' + (p.title || '').replace(/"/g, '&quot;') + '" aria-label="Share"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg></button>' +
            '</div>' +
            '<h3 class="quick-add-modal__title"' + (preAccentColor ? ' style="color:' + preAccentColor + '"' : '') + '>' + p.title + '</h3>' +
            '<div style="display:flex;align-items:center;gap:12px;margin:0 0 8px;flex-wrap:wrap"><span style="color:var(--color-gray);' + vlCss + '">' + (custom.variant_label || p.product_type || '') + '</span>' + intensityHtml + '</div>' +
            starsHtml +
            '<div style="display:flex;gap:16px;margin-bottom:20px;align-items:flex-start">' +
              '<div style="position:relative;width:120px;height:140px;flex-shrink:0">' +
                '<img src="'+(p.featured_image||'')+'" style="width:100%;height:100%;object-fit:cover;border-radius:20px 0 20px 0' + (!p.available ? ';filter:grayscale(80%) brightness(0.92)' : '') + '">' +
                (topBadge ? '<div class="product-card__badges" style="bottom:0;left:0">' + topBadge + '</div>' : '') +
                soldOutOverlay +
              '</div>' +
              '<div style="flex:1;min-width:0">' +
                originHtml +
                '<div style="font-weight:900;color:var(--color-primary);font-size:24px">₹ '+(variant.price/100).toFixed(2)+ compareHtml + '</div>' +
                saveHtml +
                (pricePerUnit ? '<p class="product-card__price-per-unit" style="margin:4px 0 0">' + pricePerUnit + '</p>' : '') +
                stockText +
                (lowStockVariant ? '<p class="product-card__stock-urgency">Only ' + lowStockVariant.inventory_quantity + ' left</p>' : '') +
                (preShortDesc ? '<div class="qv-short-desc-wrap">' +
                    '<div class="qv-short-desc" id="qv-short-desc" data-qv-desc>' + preShortDesc + '</div>' +
                    '<button type="button" class="qv-short-desc-toggle" data-qv-desc-toggle aria-controls="qv-short-desc" aria-expanded="false" hidden>See more</button>' +
                  '</div>' : '') +
              '</div>' +
            '</div>';

          if (p.variants.length > 1) {
            html += '<label style="display:block;margin-bottom:8px;font-weight:700;text-transform:uppercase;font-size:12px;letter-spacing:0.5px;color:var(--color-primary)">Grind For:</label>';
            html += '<select data-quick-variant class="js-custom-select" style="width:100%;padding:10px 14px;border:1px solid var(--color-border);border-radius:6px;font:inherit;margin-bottom:16px">';
            p.variants.forEach(function(v) {
              var label = v.title + ' — ₹ ' + (v.price/100).toFixed(2);
              if (!v.available) label += ' (Sold out)';
              html += '<option value="'+v.id+'" data-price="'+v.price+'"' + (v.compare_at_price ? ' data-compare="'+v.compare_at_price+'"' : '') + ' data-available="'+v.available+'"' + (v.id === variant.id ? ' selected' : '') + (v.available ? '' : ' disabled') + '>'+label+'</option>';
            });
            html += '</select>';
          }

          if (p.available) {
            if (preCombo) html += '<div class="combo-maker combo-maker--modal" data-combo-maker data-qv-combo hidden></div>';
            // Quantity stepper
            html += '<div style="display:flex;gap:12px;align-items:center;margin-bottom:14px">' +
              '<label style="font-weight:700;text-transform:uppercase;font-size:12px;letter-spacing:0.5px">Qty</label>' +
              '<div class="qty-stepper" data-qv-stepper>' +
                '<button type="button" class="qty-stepper__btn" data-qv-decrease aria-label="Decrease quantity">&minus;</button>' +
                '<input type="number" value="1" min="1" class="qty-stepper__input" data-qv-qty aria-label="Quantity">' +
                '<button type="button" class="qty-stepper__btn" data-qv-increase aria-label="Increase quantity">+</button>' +
              '</div>' +
            '</div>';
            html += '<button type="button" class="btn btn--primary btn--full" data-quick-add-submit data-variant-id="'+variant.id+'">Add to cart</button>';
            html += '<button type="button" class="btn btn--secondary btn--full" data-quick-buy-now data-variant-id="'+variant.id+'" style="margin-top:10px">Buy now</button>';
          } else {
            html += '<form data-back-in-stock data-product-id="' + p.id + '"><input type="email" required class="back-in-stock__input" placeholder="your@email.com"><button type="submit" class="btn btn--primary btn--full">Notify me when back in stock</button></form>';
          }
          html += '<a href="'+p.url+'" class="btn btn--secondary btn--full" style="margin-top:10px">View full details</a>';
          body.innerHTML = html;

          // Build the combo maker (config bridged from the card) into the modal
          if (preCombo && window.ComboMaker) {
            var comboEl = body.querySelector('[data-qv-combo]');
            if (comboEl) { comboEl.setAttribute('data-combo-inited', ''); try { window.ComboMaker.build(comboEl, JSON.parse(preCombo)); } catch (e) {} }
          }

          // Short description: clamp to two lines behind a See more / See less toggle, so a
          // long one can't push the add-to-cart button out of the modal.
          var descEl = body.querySelector('[data-qv-desc]');
          var descBtn = body.querySelector('[data-qv-desc-toggle]');
          if (descEl && descBtn) {
            descEl.classList.add('qv-short-desc--clamped');
            // Only offer the toggle when the text actually overflows two lines — a one-line
            // description should not get a pointless "See more". scrollHeight is only
            // meaningful once the modal has laid out, hence the frame wait.
            requestAnimationFrame(function() {
              if (descEl.scrollHeight - descEl.clientHeight > 2) descBtn.hidden = false;
            });
            descBtn.addEventListener('click', function() {
              var clamped = descEl.classList.toggle('qv-short-desc--clamped');
              descBtn.textContent = clamped ? 'See more' : 'See less';
              descBtn.setAttribute('aria-expanded', clamped ? 'false' : 'true');
            });
          }

          // Quantity stepper wiring
          var qtyInput = body.querySelector('[data-qv-qty]');
          var qDec = body.querySelector('[data-qv-decrease]');
          var qInc = body.querySelector('[data-qv-increase]');
          if (qDec) qDec.addEventListener('click', function() { var v = parseInt(qtyInput.value, 10) || 1; if (v > 1) qtyInput.value = v - 1; });
          if (qInc) qInc.addEventListener('click', function() { var v = parseInt(qtyInput.value, 10) || 1; qtyInput.value = v + 1; });
          // Initialize save text from the default variant
          var initialSave = body.querySelector('[data-qv-save]');
          if (initialSave) initialSave.textContent = buildSaveText(variant.price, variant.compare_at_price);
          // Re-init custom selects for the dynamically injected dropdown
          if (window.initCustomSelects) window.initCustomSelects();

          // Variant select switching
          var variantSelect = body.querySelector('[data-quick-variant]');
          if (variantSelect) {
            variantSelect.addEventListener('change', function() {
              var opt = variantSelect.options[variantSelect.selectedIndex];
              if (!opt) return;
              var newId = opt.value;
              var newPrice = opt.dataset.price;
              var compare = opt.dataset.compare;
              var addBtn = body.querySelector('[data-quick-add-submit]');
              var buyBtn = body.querySelector('[data-quick-buy-now]');
              if (addBtn) addBtn.setAttribute('data-variant-id', newId);
              if (buyBtn) buyBtn.setAttribute('data-variant-id', newId);
              var priceEl = body.querySelector('div[style*="font-weight:900"]');
              if (priceEl && newPrice) {
                priceEl.innerHTML = '₹ ' + (newPrice/100).toFixed(2) + (compare && parseInt(compare) > parseInt(newPrice) ? ' <s style="color:var(--color-gray);font-weight:600;font-size:16px">₹ ' + (compare/100).toFixed(2) + '</s>' : '');
                var saveEl = body.querySelector('[data-qv-save]');
                if (saveEl) saveEl.textContent = buildSaveText(parseInt(newPrice, 10), parseInt(compare, 10));
              }
            });
          }
          var addBtn = body.querySelector('[data-quick-add-submit]');
          if (addBtn) addBtn.addEventListener('click', function() {
            var id = addBtn.getAttribute('data-variant-id') || variant.id;
            var qtyEl = body.querySelector('[data-qv-qty]');
            var qty = qtyEl ? (parseInt(qtyEl.value, 10) || 1) : 1;
            var qvComboEl = body.querySelector('[data-combo-maker]');
            var qvComboProps = null;
            if (qvComboEl && window.ComboMaker) {
              var qvRes = window.ComboMaker.collect(qvComboEl);
              if (!qvRes.ok) { window.showToast && window.showToast('Please complete your combo selections', 'error'); return; }
              qvComboProps = qvRes.properties;
            }
            var qvItem = { id: parseInt(id), quantity: qty };
            if (qvComboProps) qvItem.properties = qvComboProps;
            addBtn.disabled = true;
            fetch('/cart/add.js', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ items:[qvItem] }) })
              .then(function(r){ return r.json(); })
              // No toast — the quick view closes and the cart drawer takes its place. Close
              // FIRST: close() calls lenis.start(), which would otherwise undo the drawer's
              // lenis.stop() and leave the page scrolling behind the open drawer.
              .then(function() { if (window.refreshCartState) window.refreshCartState(); close(); if (window.revealCartAfterAdd) window.revealCartAfterAdd(); })
              .catch(function() { addBtn.disabled = false; window.showToast && window.showToast('Could not add to cart', 'error'); });
          });
        })
        .catch(function() { window.renderAsyncError(body, 'Could not load product.'); });
    }
    function close() {
      if (!overlay) return;
      overlay.classList.remove('is-open');
      if (window.lenis) window.lenis.start();
      if (releaseTrap) { releaseTrap(); releaseTrap = null; }
      // Drop focus so the original product card stops matching :focus-within
      // (otherwise its hover-action stack stays visible after the modal closes).
      if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
    }
    window.openQuickAdd = open;
    document.addEventListener('click', function(e) {
      var btn = e.target.closest('[data-quick-add]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      open(btn.getAttribute('data-quick-add'), btn);
    });
  })();

  // --- Brew guide modal ---
  (function() {
    document.addEventListener('click', function(e) {
      var btn = e.target.closest('[data-brew-guide-open]');
      if (!btn) return;
      e.preventDefault();
      var source = document.querySelector('[data-brew-guide-content]');
      if (!source) return;
      var overlay = document.createElement('div');
      overlay.className = 'quick-add-overlay brew-guide-modal is-open';
      overlay.innerHTML = '<div class="quick-add-modal" role="dialog" aria-modal="true"><button type="button" class="quick-add-modal__close" aria-label="Close"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button><h3 class="quick-add-modal__title">Brew guide</h3>' + source.innerHTML + '</div>';
      document.body.appendChild(overlay);
      if (window.lenis) window.lenis.stop();
      var release = window.trapFocus(overlay.querySelector('.quick-add-modal'));
      function close() {
        overlay.remove();
        if (window.lenis) window.lenis.start();
        release && release();
      }
      overlay.querySelector('.quick-add-modal__close').addEventListener('click', close);
      overlay.addEventListener('click', function(e2) { if (e2.target === overlay) close(); });
      document.addEventListener('keydown', function onEsc(e3) { if (e3.key === 'Escape') { close(); document.removeEventListener('keydown', onEsc); } });
    });
  })();

  // --- Product compare tray ---
  (function() {
    var KEY = 'smglr-compare';
    var tray = null;
    function read() { try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch(e) { return []; } }
    function write(list) { try { localStorage.setItem(KEY, JSON.stringify(list.slice(0, 3))); } catch(e) {} render(); }
    function build() {
      if (tray) return;
      tray = document.createElement('div');
      tray.className = 'compare-tray';
      document.body.appendChild(tray);
    }
    function render() {
      build();
      var list = read();
      if (!list.length) { tray.classList.remove('is-open'); return; }
      var html = '<strong>Compare ('+list.length+'/3)</strong><div class="compare-tray__thumbs">';
      list.forEach(function(item) { html += '<img class="compare-tray__thumb" src="'+(item.image||'')+'" alt="" title="'+item.title+'">'; });
      html += '</div><div class="compare-tray__actions">';
      html += '<button type="button" class="btn btn--secondary" data-compare-view>View</button>';
      html += '<button type="button" class="btn btn--secondary" data-compare-clear>Clear</button>';
      html += '</div>';
      tray.innerHTML = html;
      tray.classList.add('is-open');
      tray.querySelector('[data-compare-clear]').addEventListener('click', function() { write([]); });
      tray.querySelector('[data-compare-view]').addEventListener('click', function() {
        window.location.href = '/pages/compare?ids=' + list.map(function(i){return i.handle;}).join(',');
      });
    }
    window.toggleCompare = function(item) {
      if (!item || !item.handle) return;
      var list = read();
      var idx = list.findIndex(function(i){ return i.handle === item.handle; });
      if (idx > -1) list.splice(idx, 1);
      else if (list.length < 3) list.push(item);
      else return window.showToast && window.showToast('You can compare up to 3 products', 'info');
      write(list);
    };
    document.addEventListener('click', function(e) {
      var btn = e.target.closest('[data-compare-toggle]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      window.toggleCompare({ handle: btn.getAttribute('data-handle'), title: btn.getAttribute('data-title'), image: btn.getAttribute('data-image') });
    });
    if (read().length) render();
  })();

  // --- Product card swatch switching (price + image + variant ID) ---
  document.addEventListener('click', function(e) {
    var swatch = e.target.closest('[data-card-swatch]');
    if (!swatch) return;
    e.preventDefault();
    e.stopPropagation();
    var card = swatch.closest('[data-product-card]');
    if (!card) return;

    // Activate this swatch
    card.querySelectorAll('[data-card-swatch]').forEach(function(s) { s.classList.remove('is-active'); });
    swatch.classList.add('is-active');

    var price = parseInt(swatch.dataset.variantPrice, 10) || 0;
    var compare = parseInt(swatch.dataset.variantCompare, 10) || 0;
    var variantId = swatch.dataset.variantId;
    var available = swatch.dataset.variantAvailable === 'true';
    var img = swatch.dataset.variantImage;

    function formatINR(cents) {
      return (cents / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    // Update price
    var priceWrap = card.querySelector('.product-card__price');
    if (priceWrap) {
      var html = '<span class="product-card__price-current"><span class="currency-symbol">₹</span> ' + formatINR(price) + '</span>';
      if (compare && compare > price) {
        html += '<span class="product-card__price-compare"><span class="currency-symbol">₹</span> ' + formatINR(compare) + '</span>';
        html += '<span class="product-card__price-save">SAVE <span class="currency-symbol">₹</span> ' + formatINR(compare - price) + '</span>';
      }
      priceWrap.innerHTML = html;
    }

    // Update image
    if (img) {
      var cardImg = card.querySelector('.product-card__image-wrapper img');
      if (cardImg) cardImg.src = img;
    }

    // Update add-to-cart + buy-now variant IDs
    card.querySelectorAll('[data-add-to-cart], [data-buy-now], [data-quick-add-submit]').forEach(function(btn) {
      btn.setAttribute('data-variant-id', variantId);
    });

    // Update available state
    var addBtn = card.querySelector('[data-add-to-cart]');
    if (addBtn) {
      if (available) {
        addBtn.disabled = false;
        addBtn.textContent = addBtn.dataset.originalLabel || 'Add to cart';
      } else {
        addBtn.disabled = true;
        addBtn.textContent = 'Sold out';
      }
    }
  });

  // --- FAQ coffee cup: interactive mouse-tracking tilt ---
  function initCoffeeCup() {
    var cup = document.querySelector('.faq-section__coffee-image');
    if (!cup || prefersReducedMotion || cup._cupInit) return;
    cup._cupInit = true;
    var section = cup.closest('.faq-section');
    if (!section || typeof gsap === 'undefined') return;

    var baseRotate = -12;
    var maxTilt = 18;
    var maxShift = 20;
    var isHovering = false;

    section.addEventListener('mousemove', function(e) {
      if (!isHovering) return;
      var rect = section.getBoundingClientRect();
      var nx = (e.clientX - rect.left) / rect.width - 0.5;
      var ny = (e.clientY - rect.top) / rect.height - 0.5;

      gsap.to(cup, {
        x: nx * maxShift,
        y: ny * maxShift * 0.6,
        rotation: baseRotate + nx * maxTilt,
        duration: 0.6,
        ease: 'power2.out',
        overwrite: 'auto'
      });
    });

    section.addEventListener('mouseenter', function() {
      isHovering = true;
      // Freeze the cup at its current animated position before killing CSS animation.
      // Read the live computed transform, apply it as inline, then disable CSS anim.
      var computed = window.getComputedStyle(cup).transform;
      cup.style.animation = 'none';
      cup.style.transform = computed;
    });

    section.addEventListener('mouseleave', function() {
      isHovering = false;
      // Snap back with elastic bounce, then restore CSS float animation
      gsap.to(cup, {
        x: 0,
        y: 0,
        rotation: baseRotate,
        duration: 1,
        ease: 'elastic.out(1, 0.4)',
        overwrite: 'auto',
        onComplete: function() {
          // Clear GSAP inline transforms and restore CSS animation
          gsap.set(cup, { clearProps: 'x,y,rotation,transform' });
          cup.style.animation = '';
        }
      });
    });

    // Click wiggle
    cup.addEventListener('click', function() {
      cup.style.animation = 'none';
      gsap.timeline()
        .to(cup, { rotation: baseRotate + 20, duration: 0.1, ease: 'power2.in' })
        .to(cup, { rotation: baseRotate - 15, duration: 0.1 })
        .to(cup, { rotation: baseRotate + 10, duration: 0.1 })
        .to(cup, { rotation: baseRotate, duration: 0.4, ease: 'elastic.out(1, 0.3)', onComplete: function() {
          gsap.set(cup, { clearProps: 'x,y,rotation,transform' });
          if (!isHovering) cup.style.animation = '';
        }});
    });
  }
  // Init after window.load (GSAP + DOM both ready)
  if (document.readyState === 'complete') initCoffeeCup();
  else window.addEventListener('load', initCoffeeCup);

  // --- Newsletter coffee cups: intro + continuous + interactive + outro ---
  function initNewsletterCups() {
    var cups = document.querySelectorAll('[data-nl-cup]');
    if (!cups.length || prefersReducedMotion || typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

    cups.forEach(function(cup) {
      if (cup._nlInit) return;
      cup._nlInit = true;
      var isLeft = cup.dataset.nlCup === 'left';
      var section = cup.closest('.newsletter-section');
      if (!section) return;

      // --- INTRO: slide in from outside when section enters viewport ---
      var startX = isLeft ? -120 : 120;
      gsap.set(cup, { x: startX, opacity: 0, scale: 0.7, rotation: isLeft ? -30 : 30 });

      ScrollTrigger.create({
        trigger: section,
        start: 'top 80%',
        once: true,
        onEnter: function() {
          gsap.to(cup, {
            x: 0,
            opacity: 1,
            scale: 1,
            rotation: 0,
            duration: 1.2,
            ease: 'elastic.out(1, 0.5)',
            onComplete: function() {
              // Clear GSAP inline props so CSS float animation takes over
              gsap.set(cup, { clearProps: 'x,opacity,scale,rotation,transform' });
            }
          });
        }
      });

      // --- INTERACTIVE: mouse tracking tilt within the newsletter section ---
      var isHovering = false;
      var baseRotate = isLeft ? -3 : 3;
      var maxTilt = 15;
      var maxShift = 25;

      section.addEventListener('mousemove', function(e) {
        if (!isHovering) return;
        var rect = section.getBoundingClientRect();
        var nx = (e.clientX - rect.left) / rect.width - 0.5;
        var ny = (e.clientY - rect.top) / rect.height - 0.5;

        gsap.to(cup, {
          x: nx * maxShift * (isLeft ? 1 : -1),
          y: ny * maxShift * 0.5,
          rotation: baseRotate + nx * maxTilt * (isLeft ? 1 : -1),
          duration: 0.5,
          ease: 'power2.out',
          overwrite: 'auto'
        });
      });

      section.addEventListener('mouseenter', function() {
        isHovering = true;
        var computed = window.getComputedStyle(cup).transform;
        cup.style.animation = 'none';
        cup.style.transform = computed;
      });

      section.addEventListener('mouseleave', function() {
        isHovering = false;
        gsap.to(cup, {
          x: 0,
          y: 0,
          rotation: baseRotate,
          duration: 1.2,
          ease: 'elastic.out(1, 0.4)',
          overwrite: 'auto',
          onComplete: function() {
            gsap.set(cup, { clearProps: 'x,y,rotation,transform' });
            cup.style.animation = '';
          }
        });
      });

      // --- CLICK: playful bounce ---
      cup.addEventListener('click', function() {
        cup.style.animation = 'none';
        var dir = isLeft ? 1 : -1;
        gsap.timeline()
          .to(cup, { y: -30, rotation: baseRotate + 15 * dir, scale: 1.1, duration: 0.2, ease: 'power2.out' })
          .to(cup, { y: 0, rotation: baseRotate - 10 * dir, scale: 1, duration: 0.15 })
          .to(cup, { y: -10, rotation: baseRotate + 5 * dir, duration: 0.12 })
          .to(cup, { y: 0, rotation: baseRotate, duration: 0.5, ease: 'elastic.out(1, 0.3)', onComplete: function() {
            gsap.set(cup, { clearProps: 'x,y,rotation,scale,transform' });
            if (!isHovering) cup.style.animation = '';
          }});
      });
    });
  }
  if (document.readyState === 'complete') initNewsletterCups();
  else window.addEventListener('load', initNewsletterCups);

  // --- Buy now: checkout ONLY the clicked product, isolated from the rest of the cart ---
  var buyNowBusy = false; // shared in-flight lock — no two Buy-now flows run at once (prevents cross-flow cart loss)
  document.addEventListener('click', function(e) {
    var btn = e.target.closest('[data-buy-now][data-variant-id], [data-quick-buy-now][data-variant-id]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    // Multi-variant card button: open the quick-add modal to pick a variant first (its Buy now finishes here).
    if (btn.hasAttribute('data-needs-options')) {
      var bnCard = btn.closest('[data-product-card]');
      var bnQa = bnCard && bnCard.querySelector('[data-quick-add]');
      if (bnQa && typeof window.openQuickAdd === 'function') window.openQuickAdd(bnQa.getAttribute('data-quick-add'), bnQa);
      return;
    }
    // Combo: require all selections, then carry them as line-item properties through the isolated buy-now add.
    // Resolve the combo ONLY from the main product form or the modal overlay — NEVER from a product card,
    // so a Buy-now on a recommended card on a combo PDP can't inherit the main product's combo.
    var bnComboEl = null;
    if (!btn.closest('[data-product-card]')) {
      var bnComboScope = btn.closest('.quick-add-overlay') || btn.closest('form');
      bnComboEl = bnComboScope && bnComboScope.querySelector('[data-combo-maker]');
    }
    var bnComboProps = null;
    if (bnComboEl && window.ComboMaker) {
      var bnRes = window.ComboMaker.collect(bnComboEl);
      if (!bnRes.ok) { window.showToast && window.showToast('Please complete your combo selections', 'error'); return; }
      bnComboProps = bnRes.properties;
    }
    function bnItem() { var it = { id: id, quantity: qty }; if (bnComboProps) it.properties = bnComboProps; return { items: [it] }; }
    if (buyNowBusy) return; // ignore a second Buy now while one is mid-flight
    var id = parseInt(btn.dataset.variantId, 10);
    if (!id) return;
    var qty = parseInt(btn.dataset.quantity, 10) || 0;
    if (!qty) { // modal Buy now: honour its quantity stepper
      var ov = btn.closest('.quick-add-overlay');
      var qEl = ov && ov.querySelector('[data-qv-qty]');
      qty = qEl ? (parseInt(qEl.value, 10) || 1) : 1;
    }
    var KEY = 'smglr-buynow-restore';
    buyNowBusy = true;
    btn.disabled = true;
    function fail() { buyNowBusy = false; btn.disabled = false; window.showToast && window.showToast('Could not buy now', 'error'); }
    function forget() { try { localStorage.removeItem(KEY); } catch (e) {} }
    // Add ONLY this item then go to checkout (used both when isolated and as the safe non-isolated fallback).
    function addOnly() {
      return fetch('/cart/add.js', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(bnItem()) })
        .then(function(r){ if (!r.ok) throw new Error('add'); window.location.href = '/checkout'; });
    }
    // Cart already emptied (snapshot saved): add ONLY this item + checkout; if the add fails, restore the snapshot.
    function addIsolated() {
      return fetch('/cart/add.js', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(bnItem()) })
        .then(function(r2){ if (!r2.ok) throw new Error('add'); window.location.href = '/checkout'; })
        .catch(function(){ if (window.__smglrRestoreCart) window.__smglrRestoreCart(); fail(); });
    }
    // A snapshot restore is mid-flight: clearing/adding under it races the same cart + KEY and could lose items.
    // Degrade to a non-isolated add+checkout (never clears, never touches the KEY) — safe, and rare enough to accept.
    if (restoreBusy) { addOnly().catch(fail); return; }
    // Snapshot the cart, empty it, add just this item, checkout. The snapshot is re-added when the shopper
    // next returns to the store (restoreBuyNowSnapshot). Never empties the cart unless the snapshot is saved.
    fetch('/cart.js').then(function(r){ return r.json(); }).then(function(cart){
      var items = cart.items || [];
      if (!items.length) { forget(); return addOnly(); } // nothing else in the cart -> already isolated
      var saved = false;
      try { localStorage.setItem(KEY, JSON.stringify(items.map(function(i){ return { id:i.id, quantity:i.quantity, properties:i.properties }; }))); saved = true; } catch (e) {}
      if (!saved) return addOnly(); // can't snapshot -> NEVER empty the cart; fall back (checkout may show other items)
      return fetch('/cart/clear.js', { method:'POST', headers:{'Content-Type':'application/json'} }).then(
        function(r){                                   // clear responded
          if (!r.ok) { forget(); return addOnly(); }   // didn't clear -> cart intact -> don't restore; non-isolated
          return addIsolated();                        // cart emptied -> isolated add + checkout
        },
        function(){                                    // clear NETWORK error -> unknown if it committed; re-check the cart
          return fetch('/cart.js').then(function(r){ return r.json(); }).then(function(c2){
            if ((c2.items || []).length) { forget(); return addOnly(); } // cart intact -> clear didn't take -> non-isolated
            return addIsolated();                      // cart WAS emptied despite the reject -> isolated (snapshot stays for restore)
          }).catch(function(){ forget(); return addOnly(); }); // can't re-check -> safest non-isolated
        }
      );
    }).catch(fail);
  });

  // --- Notify me: opens quick-add modal in waitlist mode ---
  document.addEventListener('click', function(e) {
    var btn = e.target.closest('[data-notify-toggle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var handle = btn.getAttribute('data-notify-toggle');
    var pid = btn.getAttribute('data-product-id');
    var overlay = document.createElement('div');
    overlay.className = 'quick-add-overlay is-open';
    overlay.innerHTML = '<div class="quick-add-modal" role="dialog" aria-modal="true">' +
      '<button type="button" class="quick-add-modal__close" aria-label="Close"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>' +
      '<h3 class="quick-add-modal__title">Notify me when back in stock</h3>' +
      '<p style="color:var(--color-gray);margin:0 0 16px">We will email you the moment this product is restocked.</p>' +
      '<form data-back-in-stock data-product-id="' + pid + '">' +
        '<input type="email" required class="back-in-stock__input" placeholder="your@email.com">' +
        '<button type="submit" class="btn btn--primary btn--full">Notify me</button>' +
      '</form>' +
    '</div>';
    document.body.appendChild(overlay);
    if (window.lenis) window.lenis.stop();
    var release = window.trapFocus(overlay.querySelector('.quick-add-modal'));
    function close() {
      overlay.remove();
      if (window.lenis) window.lenis.start();
      release && release();
    }
    overlay.querySelector('.quick-add-modal__close').addEventListener('click', close);
    overlay.addEventListener('click', function(ev){ if (ev.target === overlay) close(); });
  });

  // --- Product card share button ---
  document.addEventListener('click', function(e) {
    var btn = e.target.closest('[data-share-product]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var url = btn.getAttribute('data-share-url');
    var title = btn.getAttribute('data-share-title') || '';
    if (navigator.share) {
      navigator.share({ title: title, url: url }).catch(function() {});
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(url);
      window.showToast && window.showToast('Link copied', 'success');
    }
  });

  // --- Mark compare button as active when product is in compare list ---
  (function() {
    var KEY = 'smglr-compare';
    function refresh() {
      var list = []; try { list = JSON.parse(localStorage.getItem(KEY) || '[]'); } catch(e) {}
      var handles = list.map(function(i){ return i.handle; });
      document.querySelectorAll('[data-compare-toggle]').forEach(function(btn) {
        btn.classList.toggle('is-active', handles.indexOf(btn.getAttribute('data-handle')) > -1);
      });
    }
    document.addEventListener('click', function(e) { if (e.target.closest('[data-compare-toggle]')) setTimeout(refresh, 50); });
    refresh();
  })();

  // --- Back-in-stock waitlist ---
  document.addEventListener('submit', function(e) {
    var form = e.target.closest('[data-back-in-stock]');
    if (!form) return;
    e.preventDefault();
    var email = form.querySelector('input[type=email]').value;
    var productId = form.getAttribute('data-product-id');
    // Store locally; a real store would POST to a backend / app proxy
    try {
      var list = JSON.parse(localStorage.getItem('smglr-bis') || '[]');
      list.push({ product_id: productId, email: email, at: Date.now() });
      localStorage.setItem('smglr-bis', JSON.stringify(list));
    } catch(err) {}
    window.showToast && window.showToast('We will notify you when it is back in stock', 'success');
    form.reset();
  });

  // --- Rates timeout retry (for cart pincode) ---
  // The existing main-cart.liquid script uses fetch. Overriding globally is fragile;
  // instead expose a helper other code can opt into. Already exported fetchWithTimeout above.

  // --- Reorder button ---
  document.addEventListener('click', function(e) {
    var btn = e.target.closest('[data-reorder]');
    if (!btn) return;
    e.preventDefault();
    var items;
    try { items = JSON.parse(btn.getAttribute('data-reorder')); } catch(err) { return; }
    btn.disabled = true;
    fetch('/cart/add.js', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ items: items }) })
      .then(function(r){ return r.json(); })
      .then(function() {
        // No success toast by design; landing on the cart is the confirmation. The 600ms
        // delay existed only so the toast could be read, so it goes with it.
        window.location.href = '/cart';
      })
      .catch(function() { btn.disabled = false; window.showToast && window.showToast('Could not reorder', 'error'); });
  });

  // --- Swiper carousel init ---
  /* Every Swiper here sets roundLengths: true.
     Swiper positions slides with translate3d(-slideWidth, 0, 0). Slide widths are derived
     from container widths that are usually fractional — the Bean Guide slide, for instance,
     is `100vw - 2 * clamp(18px, 4.2vw, 76px)`, which is 1319.04px at a 1440 viewport. A
     composited layer offset by a fraction of a pixel gets resampled, which softens
     EVERYTHING on it: text, vector chrome, buttons and photos alike, and only at the widths
     where the arithmetic lands off-pixel. Measured at ~17% loss of edge contrast.
     roundLengths snaps slide widths and translates to whole pixels, which removes it. */
  function initSwipers() {
    if (typeof Swiper === 'undefined') return;

    // Testimonials
    document.querySelectorAll('[data-swiper="testimonials"]:not(.swiper-initialized)').forEach(function(el) {
      new Swiper(el, {
        roundLengths: true,   // see note above initSwipers
        slidesPerView: 1,
        spaceBetween: 40,
        grabCursor: true,
        loop: true,
        autoplay: { delay: 4000, disableOnInteraction: false, pauseOnMouseEnter: true },
        pagination: { el: el.querySelector('.swiper-pagination'), clickable: true },
        navigation: { prevEl: el.querySelector('.swiper-button-prev'), nextEl: el.querySelector('.swiper-button-next') },
        breakpoints: {
          768: { slidesPerView: 2, spaceBetween: 50 },
          992: { slidesPerView: 3, spaceBetween: 60 },
          1200: { slidesPerView: 4, spaceBetween: 80 }
        },
        a11y: { prevSlideMessage: 'Previous testimonial', nextSlideMessage: 'Next testimonial' }
      });
    });

    // Blog posts
    document.querySelectorAll('[data-swiper="blog"]:not(.swiper-initialized)').forEach(function(el) {
      new Swiper(el, {
        roundLengths: true,   // see note above initSwipers
        slidesPerView: 1,
        spaceBetween: 24,
        grabCursor: true,
        loop: true,
        autoplay: { delay: 5000, disableOnInteraction: false, pauseOnMouseEnter: true },
        pagination: { el: el.querySelector('.swiper-pagination'), clickable: true },
        navigation: { prevEl: el.querySelector('.swiper-button-prev'), nextEl: el.querySelector('.swiper-button-next') },
        breakpoints: {
          768: { slidesPerView: 2 },
          1200: { slidesPerView: 3 }
        },
        a11y: { prevSlideMessage: 'Previous post', nextSlideMessage: 'Next post' }
      });
    });

    // Product recommendations ("You may also like")
    document.querySelectorAll('[data-swiper="recs"]:not(.swiper-initialized)').forEach(function(el) {
      new Swiper(el, {
        roundLengths: true,   // see note above initSwipers
        slidesPerView: 1,
        spaceBetween: 20,
        grabCursor: true,
        pagination: { el: el.querySelector('.swiper-pagination'), clickable: true },
        navigation: { prevEl: el.querySelector('.swiper-button-prev'), nextEl: el.querySelector('.swiper-button-next') },
        breakpoints: {
          768: { slidesPerView: 2 },
          992: { slidesPerView: 3 }
        },
        a11y: { prevSlideMessage: 'Previous product', nextSlideMessage: 'Next product' }
      });
    });

    // Recently viewed
    document.querySelectorAll('[data-swiper="recent"]:not(.swiper-initialized)').forEach(function(el) {
      new Swiper(el, {
        roundLengths: true,   // see note above initSwipers
        slidesPerView: 1,
        spaceBetween: 20,
        grabCursor: true,
        pagination: { el: el.querySelector('.swiper-pagination'), clickable: true },
        breakpoints: {
          768: { slidesPerView: 2 },
          992: { slidesPerView: 3 }
        }
      });
    });

    // PDP thumbnails (mobile only)
    if (window.innerWidth <= 767) {
      document.querySelectorAll('.product-page__thumbs:not(.swiper-initialized)').forEach(function(el) {
        el.classList.add('swiper');
        // Wrap children in swiper-wrapper if not already
        if (!el.querySelector('.swiper-wrapper')) {
          var wrapper = document.createElement('div');
          wrapper.className = 'swiper-wrapper';
          while (el.firstChild) wrapper.appendChild(el.firstChild);
          el.appendChild(wrapper);
          // Wrap each thumb in swiper-slide
          wrapper.querySelectorAll('.product-page__thumb').forEach(function(thumb) {
            var slide = document.createElement('div');
            slide.className = 'swiper-slide';
            thumb.parentNode.insertBefore(slide, thumb);
            slide.appendChild(thumb);
          });
        }
        new Swiper(el, {
        roundLengths: true,   // see note above initSwipers
          slidesPerView: 'auto',
          spaceBetween: 10,
          freeMode: true,
          watchOverflow: true
        });
      });
    }

    // Bean Guide (PDP "Bean Guide" tab) — one artboard per slide.
    // Uses its own nav + dots because theme.css hides .swiper-button-* and
    // .swiper-pagination globally with !important.
    document.querySelectorAll('[data-swiper="bean-guide"]:not(.swiper-initialized)').forEach(function(el) {
      var root = el.closest('[data-bean-guide]') || el.parentNode;
      var prev = root.querySelector('[data-bean-guide-prev]');
      var next = root.querySelector('[data-bean-guide-next]');
      if (el.querySelectorAll('.bean-guide__slide').length < 2) return;

      // Do NOT initialise inside a display:none tab panel. Swiper would measure zero and
      // write `width: 0px` onto every slide, so the card SVGs would lay out at ~0px until
      // update() runs after the tab is opened. Desktop Chrome re-rasterises an <img> SVG
      // when its box grows; mobile browsers commonly keep the bitmap they already made and
      // scale it up, which makes the whole card -- vector text included -- look soft while
      // the unscaled chrome stays sharp. Waiting for a real width avoids that entirely.
      // The tab handler below calls initSwipers() again once a panel is opened.
      if (!el.getBoundingClientRect().width) return;

      var sw = new Swiper(el, {
        roundLengths: true,   // see note above initSwipers
        slidesPerView: 1,
        spaceBetween: 0,
        grabCursor: true,
        observer: true,
        observeParents: true,
        keyboard: { enabled: true },
        a11y: { prevSlideMessage: 'Previous bean guide slide', nextSlideMessage: 'Next bean guide slide' }
      });

      function paint() {
        if (prev) prev.disabled = sw.isBeginning;
        if (next) next.disabled = sw.isEnd;
      }
      sw.on('slideChange', paint);
      paint();

      if (prev) prev.addEventListener('click', function() { sw.slidePrev(); });
      if (next) next.addEventListener('click', function() { sw.slideNext(); });

      root.__beanSwiper = sw;
    });
  }

  // Force the card SVGs to be rasterised again at their current size. A browser that
  // decoded them while the tab panel was still collapsed can otherwise keep scaling that
  // first bitmap up. Re-assigning src re-decodes from cache (no network), and it is done
  // once per panel so there is no repeated flicker.
  function refreshCardRaster(root) {
    if (root.hasAttribute('data-cards-rerastered')) return;
    root.setAttribute('data-cards-rerastered', '');
    root.querySelectorAll('.bean-guide__card img').forEach(function(img) {
      var src = img.getAttribute('src');
      if (!src) return;
      img.removeAttribute('src');
      void img.offsetWidth;              // flush the removal before re-assigning
      img.setAttribute('src', src);
    });
  }

  // The Bean Guide sits in a tab panel that is display:none until opened. Its Swiper is
  // deliberately not created until then (see above), so opening a tab has to both create
  // it and re-measure any that already exist.
  document.addEventListener('click', function(e) {
    if (!e.target.closest('[data-tab]')) return;
    setTimeout(function() {
      initSwipers();                     // picks up any Bean Guide that was hidden at load
      document.querySelectorAll('[data-bean-guide]').forEach(function(r) {
        if (r.__beanSwiper) {
          r.__beanSwiper.update();
          r.__beanSwiper.slideTo(r.__beanSwiper.activeIndex, 0);
        }
        if (r.getBoundingClientRect().width) refreshCardRaster(r);
      });
    }, 30);
  });
  // Init on load + re-init after async content (recs, recently viewed)
  if (document.readyState === 'complete') initSwipers();
  else window.addEventListener('load', initSwipers);
  // Re-init after async sections inject their HTML
  setTimeout(initSwipers, 2500);
  setTimeout(initSwipers, 5000);
  window.initSwipers = initSwipers;

  // --- Mobile filter drawer focus trap (collection page) ---
  var filtersObs = new MutationObserver(function() {
    var drawer = document.querySelector('.drop-filters.is-drawer-open');
    if (drawer && !drawer._trapped) {
      drawer._trapped = window.trapFocus(drawer);
      drawer.addEventListener('trap:escape', function() { drawer.classList.remove('is-drawer-open'); });
    } else if (!drawer) {
      document.querySelectorAll('.drop-filters').forEach(function(d) {
        if (d._trapped) { d._trapped(); d._trapped = null; }
      });
    }
  });
  filtersObs.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['class'] });

})();
